import DefaultScreen from '@components/Global/DefaultScreenView';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { View, Text, Pressable, Platform, Dimensions, StyleSheet, TextInput, ScrollView, Alert, Clipboard } from 'react-native';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { selectUser } from '@reduxLocal/selectors/userSelectors';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { Card, XStack, YStack, SizableText, Button, H2, H3, Paragraph, Switch, Input } from 'tamagui';
import * as Animatable from 'react-native-animatable';
import { MaterialIcons, FontAwesome5, Ionicons, Entypo } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import styles, { getModernCardStyle, getModernActionStyle, getModernTextColor } from '@stylesheet';
import { useTranslation } from 'react-i18next';
import { createAPIKey, getAPIKeys, deleteAPIKey, toggleAPIKey, regenerateAPIKey, getOAuthTokens, revokeOAuthToken, createWebhook, getWebhooks, getAvailableEvents, updateWebhook, deleteWebhook, toggleWebhook, regenerateWebhookSecret, testWebhook, createAIModelKey, getAIModelKeys, updateAIModelKey, deleteAIModelKey, toggleAIModelKey } from '@api/api';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

// Hero Button Styles
const HERO_PRIMARY_COLOR = '#5CC0EB'; // Sky Blue from Hero.tsx
const HERO_TEXT_COLOR = '#FFFFFF';

const getHeroButtonStyle = (theme: boolean, variant: 'solid' | 'outline' = 'solid') => {
  if (variant === 'solid') {
    return {
      backgroundColor: HERO_PRIMARY_COLOR,
      shadowColor: HERO_PRIMARY_COLOR,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 6,
      borderWidth: 0,
    };
  } else {
    return {
      backgroundColor: theme ? 'rgba(255, 255, 255, 0.5)' : 'rgba(30, 41, 59, 0.5)',
      borderWidth: 2,
      borderColor: 'rgba(92, 192, 235, 0.3)',
    };
  }
};

// OAuth Configuration Interface
interface OAuthConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  scopes: string[];
  authUrl: string;
  tokenUrl: string;
  enabled: boolean;
}

// Token Information Interface
interface TokenInfo {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
  scope: string;
  issuedAt: number;
}

// API Endpoint Configuration
interface APIEndpoint {
  id: string;
  name: string;
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers: Record<string, string>;
  enabled: boolean;
  description: string;
}

// Backend API Key Interface
interface BackendAPIKey {
  id: string;
  name: string;
  keyPreview: string;
  fullKey?: string;
  permissions: string[];
  createdAt: number;
  lastUsed?: number;
  expiresAt?: number;
  isActive: boolean;
  usage: {
    totalRequests: number;
    dailyLimit: number;
    monthlyLimit: number;
    lastReset?: number;
  };
}

// API Key Creation Request
interface CreateAPIKeyRequest {
  name: string;
  permissions: string[];
  expiresIn?: number; // Days until expiration
  dailyLimit?: number;
  monthlyLimit?: number;
  description?: string;
}

// Webhook Information Interface
interface WebhookInfo {
  id: string;
  url: string;
  events: string[];
  description?: string;
  isActive: boolean;
  createdAt: number;
  lastTriggered?: number;
  stats: {
    totalRequests: number;
    successfulRequests: number;
    failedRequests: number;
    successRate: number;
    lastSuccess?: number;
    lastFailure?: number;
  };
}

// Webhook Creation Request
interface CreateWebhookRequest {
  url: string;
  events: string[];
  description?: string;
  isActive?: boolean;
  secret?: string;
}

// AI Model Key Interface
interface AIModelKey {
  id: string;
  provider: string;
  modelName: string;
  keyPreview: string;
  isDefault: boolean;
  createdAt: Date | string | number;
  lastUsed?: Date | string | number;
  isActive: boolean;
  source: 'user' | 'company';
}

// AI Model Key Creation Request
interface CreateAIModelKeyRequest {
  provider: string;
  modelName: string;
  apiKey: string;
}

// Available OAuth Providers
const OAUTH_PROVIDERS = [
  {
    id: 'google',
    name: 'Google',
    icon: 'google',
    iconLibrary: 'FontAwesome5',
    color: '#4285F4',
    gradientColors: ['#4285F4', '#34A853'],
    defaultScopes: ['openid', 'profile', 'email'],
    authUrl: 'https://accounts.google.com/oauth2/auth',
    tokenUrl: 'https://oauth2.googleapis.com/token'
  },
  {
    id: 'microsoft',
    name: 'Microsoft',
    icon: 'microsoft',
    iconLibrary: 'FontAwesome5',
    color: '#0078D4',
    gradientColors: ['#0078D4', '#106EBE'],
    defaultScopes: ['openid', 'profile', 'email'],
    authUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    tokenUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/token'
  },
  {
    id: 'github',
    name: 'GitHub',
    icon: 'github',
    iconLibrary: 'FontAwesome5',
    color: '#24292e',
    gradientColors: ['#24292e', '#586069'],
    defaultScopes: ['user:email', 'read:user'],
    authUrl: 'https://github.com/login/oauth/authorize',
    tokenUrl: 'https://github.com/login/oauth/access_token'
  },
  {
    id: 'slack',
    name: 'Slack',
    icon: 'slack',
    iconLibrary: 'FontAwesome5',
    color: '#4A154B',
    gradientColors: ['#4A154B', '#ECB22E'],
    defaultScopes: ['identity.basic', 'identity.email'],
    authUrl: 'https://slack.com/oauth/v2/authorize',
    tokenUrl: 'https://slack.com/api/oauth.v2.access'
  }
];

// Available Backend API Permissions
const BACKEND_API_PERMISSIONS = [
  {
    id: 'users:read',
    name: 'Read Users',
    description: 'View user profiles and basic information',
    category: 'Users',
    color: '#3b82f6'
  },
  {
    id: 'users:write',
    name: 'Modify Users',
    description: 'Create, update, and delete user accounts',
    category: 'Users',
    color: '#f59e0b'
  },
  {
    id: 'projects:read',
    name: 'Read Projects',
    description: 'Access project data and configurations',
    category: 'Projects',
    color: '#10b981'
  },
  {
    id: 'projects:write',
    name: 'Modify Projects',
    description: 'Create, update, and delete projects',
    category: 'Projects',
    color: '#f59e0b'
  },
  {
    id: 'analytics:read',
    name: 'Read Analytics',
    description: 'Access analytics data and reports',
    category: 'Analytics',
    color: '#8b5cf6'
  },
  {
    id: 'integrations:read',
    name: 'Read Integrations',
    description: 'View integration configurations',
    category: 'Integrations',
    color: '#06b6d4'
  },
  {
    id: 'integrations:write',
    name: 'Modify Integrations',
    description: 'Configure and manage integrations',
    category: 'Integrations',
    color: '#f59e0b'
  },
  {
    id: 'admin:read',
    name: 'Admin Read',
    description: 'View administrative settings and logs',
    category: 'Administration',
    color: '#ef4444'
  },
  {
    id: 'admin:write',
    name: 'Admin Write',
    description: 'Modify system settings and configurations',
    category: 'Administration',
    color: '#dc2626'
  },
  {
    id: 'data:read',
    name: 'Read Data',
    description: 'Access data items and folders',
    category: 'Data',
    color: '#06b6d4'
  },
  {
    id: 'data:write',
    name: 'Modify Data',
    description: 'Create, update, and delete data items',
    category: 'Data',
    color: '#f59e0b'
  },
  {
    id: 'organiser:read',
    name: 'Read Organiser',
    description: 'Access calendar and scheduling data',
    category: 'Organiser',
    color: '#8b5cf6'
  },
  {
    id: 'organiser:write',
    name: 'Modify Organiser',
    description: 'Create and manage calendar events',
    category: 'Organiser',
    color: '#f59e0b'
  },
  {
    id: 'knowledge:read',
    name: 'Read Knowledge',
    description: 'Access knowledge base articles',
    category: 'Knowledge',
    color: '#10b981'
  },
  {
    id: 'knowledge:write',
    name: 'Modify Knowledge',
    description: 'Create and update knowledge articles',
    category: 'Knowledge',
    color: '#f59e0b'
  },
  {
    id: 'company:read',
    name: 'Read Company',
    description: 'Access company information and settings',
    category: 'Company',
    color: '#3b82f6'
  },
  {
    id: 'company:write',
    name: 'Modify Company',
    description: 'Update company settings and configurations',
    category: 'Company',
    color: '#f59e0b'
  }
];

// Header Component with API Service Overview
interface HeaderComponentProps {
  theme: boolean;
  oauthStats: {
    connectedProviders: number;
    totalTokens: number;
    activeEndpoints: number;
    lastSync: string;
  };
  backendAPIKeys: BackendAPIKey[];
}

const HeaderComponent: React.FC<HeaderComponentProps> = React.memo(({ theme, oauthStats, backendAPIKeys }) => {
  return (
    <Animatable.View
      animation="fadeInDown"
      duration={800}
      style={{
        backgroundColor: theme ? "#ffffff" : "#1e293b",
        borderRadius: 20,
        padding: 24,
        marginVertical: 16,
        marginHorizontal: 20,
        borderWidth: 1,
        borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
        userSelect: 'none'
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 20 }}>
        <View style={{
          backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
          borderRadius: 12,
          padding: 12,
          marginRight: 16
        }}>
          <MaterialIcons
            name="api"
            size={28}
            color={theme ? "#0ea5e9" : "#38bdf8"}
          />
        </View>
        <View style={{ flex: 1 }}>
          <SizableText style={{
            color: theme ? "#1e293b" : "#f1f5f9",
            fontWeight: '700',
            marginBottom: 4,
            userSelect: 'none',
            pointerEvents: 'none'
          }}>
            API Management
          </SizableText>
          <SizableText numberOfLines={2} style={{
            color: theme ? "#64748b" : "#94a3b8",
            fontSize: 14,
            userSelect: 'none',
            pointerEvents: 'none'
          }}>
            Manage API tokens, backend API keys, and service integrations
          </SizableText>
        </View>
      </View>

      {/* Stats Grid */}
      <XStack gap={12} flexWrap="wrap" justifyContent="center" alignItems="center">
        {[
          { label: 'Active Tokens', value: oauthStats.totalTokens, icon: 'vpn-key', color: theme ? "#3b82f6" : "#60a5fa" },
          { label: 'API Keys', value: backendAPIKeys.length, icon: 'key', color: theme ? "#10b981" : "#34d399" },
          { label: 'API Endpoints', value: oauthStats.activeEndpoints, icon: 'settings-ethernet', color: theme ? "#f59e0b" : "#fbbf24" },
        ].map((stat, index) => (
          <Animatable.View
            key={index}
            animation="bounceIn"
            delay={200 + index * 100}
            style={{
              flexBasis: Platform.OS === 'web' ? '30%' : '100%',
              flexGrow: 1,
              minWidth: 140,
              backgroundColor: theme ? "rgba(255,255,255,0.8)" : "rgba(30,41,59,0.8)",
              borderRadius: 16,
              padding: 16,
              marginBottom: 8,
              borderWidth: 1,
              borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
              alignItems: 'center'
            }}
          >
            <MaterialIcons
              name={stat.icon as any}
              size={24}
              color={stat.color}
              style={{ marginBottom: 8 }}
            />
            <Text style={{
              fontSize: 20,
              fontWeight: '800',
              color: stat.color,
              userSelect: 'none',
              pointerEvents: 'none'
            }}>
              {stat.value}
            </Text>
            <Text style={{
              fontSize: 12,
              color: theme ? "#64748b" : "#94a3b8",
              textAlign: 'center',
              userSelect: 'none',
              pointerEvents: 'none'
            }}>
              {stat.label}
            </Text>
          </Animatable.View>
        ))}
      </XStack>

      <View style={{
        marginTop: 16,
        padding: 12,
        backgroundColor: theme ? "rgba(34,197,94,0.1)" : "rgba(34,197,94,0.2)",
        borderRadius: 12,
        borderWidth: 1,
        borderColor: theme ? "rgba(34,197,94,0.2)" : "rgba(34,197,94,0.3)"
      }}>
        <XStack alignItems="center" gap={8}>
          <MaterialIcons name="check-circle" size={16} color="#22c55e" />
          <Text style={{
            fontSize: 12,
            color: theme ? "#064e3b" : "#bbf7d0",
            fontWeight: '600',
            userSelect: 'none',
            pointerEvents: 'none'
          }}>
            API Service Status: Active • Last Sync: {oauthStats.lastSync}
          </Text>
        </XStack>
      </View>
    </Animatable.View>
  );
});

// Backend API Key Creation Component
interface APIKeyCreatorProps {
  theme: boolean;
  onCreateKey: (request: CreateAPIKeyRequest) => void;
  isVisible: boolean;
  onClose: () => void;
}

const APIKeyCreator: React.FC<APIKeyCreatorProps> = ({ theme, onCreateKey, isVisible, onClose }) => {
  const [keyName, setKeyName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [expiresIn, setExpiresIn] = useState<number | undefined>(365); // Default 1 year
  const [dailyLimit, setDailyLimit] = useState<number | undefined>(1000);
  const [monthlyLimit, setMonthlyLimit] = useState<number | undefined>(30000);

  const handlePermissionToggle = (permissionId: string) => {
    setSelectedPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(p => p !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSelectAll = () => {
    const allPermissionIds = BACKEND_API_PERMISSIONS.map(p => p.id);
    setSelectedPermissions(allPermissionIds);
  };

  const handleClearAll = () => {
    setSelectedPermissions([]);
  };

  const handleSelectCategory = (category: string) => {
    const categoryPermissions = BACKEND_API_PERMISSIONS
      .filter(p => p.category === category)
      .map(p => p.id);

    setSelectedPermissions(prev => {
      const otherPermissions = prev.filter(p => !categoryPermissions.includes(p));
      return [...otherPermissions, ...categoryPermissions];
    });
  };

  const handleClearCategory = (category: string) => {
    const categoryPermissions = BACKEND_API_PERMISSIONS
      .filter(p => p.category === category)
      .map(p => p.id);

    setSelectedPermissions(prev =>
      prev.filter(p => !categoryPermissions.includes(p))
    );
  };

  const handleCreateKey = () => {
    if (!keyName.trim()) {
      Alert.alert('Validation Error', 'Please provide a name for the API key.');
      return;
    }

    if (selectedPermissions.length === 0) {
      Alert.alert('Validation Error', 'Please select at least one permission.');
      return;
    }

    const request: CreateAPIKeyRequest = {
      name: keyName.trim(),
      permissions: selectedPermissions,
      expiresIn,
      dailyLimit,
      monthlyLimit,
      description: description.trim() || undefined
    };

    onCreateKey(request);
    
    // Reset form
    setKeyName('');
    setDescription('');
    setSelectedPermissions([]);
    setExpiresIn(365);
    setDailyLimit(1000);
    setMonthlyLimit(30000);
    onClose();
  };

  const groupedPermissions = BACKEND_API_PERMISSIONS.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {} as Record<string, typeof BACKEND_API_PERMISSIONS>);

  if (!isVisible) return null;

  return (
    <View style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 1000,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20
    }}>
      <View style={[
        getModernCardStyle(theme, 'large'),
        {
          width: '100%',
          maxWidth: 500,
          maxHeight: '90%',
          userSelect: 'none'
        }
      ]}>
        <ScrollView showsVerticalScrollIndicator={false}>
          {/* Header */}
          <XStack alignItems="center" justifyContent="space-between" marginBottom={20}>
            <H2 style={{
              color: getModernTextColor(theme),
              userSelect: 'none',
              pointerEvents: 'none'
            }}>
              Create API Key
            </H2>
            <Pressable
              onPress={onClose}
              style={[
                getModernActionStyle(theme, 'grey')
              ]}
            >
              <MaterialIcons name="close" size={20} color={getModernTextColor(theme, 'muted')} />
            </Pressable>
          </XStack>

          {/* Basic Information */}
          <YStack gap={16} marginBottom={24}>
            <YStack gap={4}>
              <Text style={{
                fontSize: 12,
                fontWeight: '600',
                color: getModernTextColor(theme, 'secondary'),
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                userSelect: 'none',
                pointerEvents: 'none'
              }}>
                API Key Name *
              </Text>
              <TextInput
                style={[
                  styles.validationTextInput,
                  {
                    backgroundColor: theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                    borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                    color: getModernTextColor(theme)
                  }
                ]}
                value={keyName}
                onChangeText={setKeyName}
                placeholder="e.g., Mobile App API Key"
                placeholderTextColor={getModernTextColor(theme, 'muted')}
              />
            </YStack>

            <YStack gap={4}>
              <Text style={{
                fontSize: 12,
                fontWeight: '600',
                color: getModernTextColor(theme, 'secondary'),
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                userSelect: 'none',
                pointerEvents: 'none'
              }}>
                Description
              </Text>
              <TextInput
                style={[
                  styles.validationTextInput,
                  {
                    backgroundColor: theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                    borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                    color: getModernTextColor(theme),
                    minHeight: 80
                  }
                ]}
                value={description}
                onChangeText={setDescription}
                placeholder="Optional description for this API key..."
                placeholderTextColor={getModernTextColor(theme, 'muted')}
                multiline
                numberOfLines={3}
              />
            </YStack>
          </YStack>

          {/* Permissions */}
          <YStack gap={16} marginBottom={24}>
            <XStack justifyContent="space-between" alignItems="center">
              <Text style={{
                fontSize: 16,
                fontWeight: '600',
                color: getModernTextColor(theme),
                userSelect: 'none',
                pointerEvents: 'none'
              }}>
                Permissions ({selectedPermissions.length} selected)
              </Text>

              <XStack gap={8}>
                <Pressable
                  onPress={handleClearAll}
                  style={{
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    backgroundColor: theme ? '#f3f4f6' : '#374151',
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: theme ? '#d1d5db' : '#4b5563'
                  }}
                >
                  <Text style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: getModernTextColor(theme, 'muted')
                  }}>
                    Clear All
                  </Text>
                </Pressable>

                <Pressable
                  onPress={handleSelectAll}
                  style={{
                    ...getHeroButtonStyle(theme, 'solid'),
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderRadius: 8
                  }}
                >
                  <Text style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: HERO_TEXT_COLOR
                  }}>
                    Select All
                  </Text>
                </Pressable>
              </XStack>
            </XStack>

            {Object.entries(groupedPermissions).map(([category, permissions]) => {
              const categorySelectedCount = permissions.filter(p => selectedPermissions.includes(p.id)).length;
              const isCategoryFullySelected = categorySelectedCount === permissions.length;

              return (
                <View key={category}>
                  <XStack justifyContent="space-between" alignItems="center" marginBottom={8}>
                    <Text style={{
                      fontSize: 14,
                      fontWeight: '600',
                      color: getModernTextColor(theme, 'secondary'),
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}>
                      {category} ({categorySelectedCount}/{permissions.length})
                    </Text>

                    <XStack gap={4}>
                      <Pressable
                        onPress={() => handleClearCategory(category)}
                        style={{
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          backgroundColor: theme ? '#fef2f2' : '#7f1d1d',
                          borderRadius: 6,
                          borderWidth: 1,
                          borderColor: theme ? '#fecaca' : '#dc2626'
                        }}
                      >
                        <Text style={{
                          fontSize: 10,
                          fontWeight: '600',
                          color: '#dc2626'
                        }}>
                          Clear
                        </Text>
                      </Pressable>

                      <Pressable
                        onPress={() => handleSelectCategory(category)}
                        style={{
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          backgroundColor: isCategoryFullySelected ? '#dcfce7' : '#3b82f6',
                          borderRadius: 6,
                          borderWidth: 1,
                          borderColor: isCategoryFullySelected ? '#bbf7d0' : '#2563eb'
                        }}
                      >
                        <Text style={{
                          fontSize: 10,
                          fontWeight: '600',
                          color: isCategoryFullySelected ? '#16a34a' : 'white'
                        }}>
                          {isCategoryFullySelected ? 'All' : 'Select'}
                        </Text>
                      </Pressable>
                    </XStack>
                  </XStack>
                
                <YStack gap={8} marginBottom={16}>
                  {permissions.map((permission) => (
                    <Pressable
                      key={permission.id}
                      onPress={() => handlePermissionToggle(permission.id)}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 12,
                        backgroundColor: selectedPermissions.includes(permission.id)
                          ? permission.color + '20'
                          : theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                        borderRadius: 12,
                        borderWidth: 1,
                        borderColor: selectedPermissions.includes(permission.id)
                          ? permission.color + '40'
                          : theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                        userSelect: 'none'
                      }}
                    >
                      <View style={{
                        width: 20,
                        height: 20,
                        borderRadius: 4,
                        backgroundColor: selectedPermissions.includes(permission.id)
                          ? permission.color
                          : 'transparent',
                        borderWidth: 2,
                        borderColor: permission.color,
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: 12
                      }}>
                        {selectedPermissions.includes(permission.id) && (
                          <MaterialIcons name="check" size={12} color="white" />
                        )}
                      </View>
                      
                      <YStack flex={1}>
                        <Text style={{
                          fontSize: 14,
                          fontWeight: '600',
                          color: getModernTextColor(theme),
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }}>
                          {permission.name}
                        </Text>
                        <Text style={{
                          fontSize: 12,
                          color: getModernTextColor(theme, 'muted'),
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }}>
                          {permission.description}
                        </Text>
                      </YStack>
                    </Pressable>
                  ))}
                </YStack>
              </View>
              );
            })}
          </YStack>

          {/* Limits */}
          <YStack gap={16} marginBottom={24}>
            <Text style={{
              fontSize: 16,
              fontWeight: '600',
              color: getModernTextColor(theme),
              userSelect: 'none',
              pointerEvents: 'none'
            }}>
              Usage Limits
            </Text>

            <XStack gap={12}>
              <YStack flex={1} gap={4}>
                <Text style={{
                  fontSize: 12,
                  fontWeight: '600',
                  color: getModernTextColor(theme, 'secondary'),
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  userSelect: 'none',
                  pointerEvents: 'none'
                }}>
                  Daily Limit
                </Text>
                <TextInput
                  style={[
                    styles.validationTextInput,
                    {
                      backgroundColor: theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                      borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                      color: getModernTextColor(theme)
                    }
                  ]}
                  value={dailyLimit?.toString() || ''}
                  onChangeText={(text) => setDailyLimit(text ? parseInt(text) : undefined)}
                  placeholder="1000"
                  placeholderTextColor={getModernTextColor(theme, 'muted')}
                  keyboardType="numeric"
                />
              </YStack>

              <YStack flex={1} gap={4}>
                <Text style={{
                  fontSize: 12,
                  fontWeight: '600',
                  color: getModernTextColor(theme, 'secondary'),
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  userSelect: 'none',
                  pointerEvents: 'none'
                }}>
                  Monthly Limit
                </Text>
                <TextInput
                  style={[
                    styles.validationTextInput,
                    {
                      backgroundColor: theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                      borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                      color: getModernTextColor(theme)
                    }
                  ]}
                  value={monthlyLimit?.toString() || ''}
                  onChangeText={(text) => setMonthlyLimit(text ? parseInt(text) : undefined)}
                  placeholder="30000"
                  placeholderTextColor={getModernTextColor(theme, 'muted')}
                  keyboardType="numeric"
                />
              </YStack>
            </XStack>

            <YStack gap={4}>
              <Text style={{
                fontSize: 12,
                fontWeight: '600',
                color: getModernTextColor(theme, 'secondary'),
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                userSelect: 'none',
                pointerEvents: 'none'
              }}>
                Expires In (Days)
              </Text>
              <TextInput
                style={[
                  styles.validationTextInput,
                  {
                    backgroundColor: theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                    borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                    color: getModernTextColor(theme)
                  }
                ]}
                value={expiresIn?.toString() || ''}
                onChangeText={(text) => setExpiresIn(text ? parseInt(text) : undefined)}
                placeholder="365 (1 year)"
                placeholderTextColor={getModernTextColor(theme, 'muted')}
                keyboardType="numeric"
              />
            </YStack>
          </YStack>

          {/* Actions */}
          <XStack gap={12} justifyContent="flex-end">
            <Pressable
              onPress={onClose}
              style={[
                getHeroButtonStyle(theme, 'outline'),
                {
                  paddingHorizontal: 24,
                  paddingVertical: 12,
                  borderRadius: 12,
                  minWidth: 80,
                  alignItems: 'center',
                  justifyContent: 'center'
                }
              ]}
            >
              <Text style={{
                color: getModernTextColor(theme),
                fontSize: 16,
                fontWeight: '600',
                userSelect: 'none',
                pointerEvents: 'none',
                textAlign: 'center'
              }}>
                Cancel
              </Text>
            </Pressable>
            
            <Pressable
              onPress={handleCreateKey}
              style={[
                getHeroButtonStyle(theme, 'solid'),
                {
                  paddingHorizontal: 24,
                  paddingVertical: 12,
                  borderRadius: 12,
                  minWidth: 160,
                  alignItems: 'center',
                  justifyContent: 'center'
                }
              ]}
            >
              <XStack alignItems="center" gap={8}>
                <MaterialIcons name="vpn-key" size={18} color={HERO_TEXT_COLOR} />
                <Text style={{
                  color: HERO_TEXT_COLOR,
                  fontSize: 16,
                  fontWeight: '600',
                  userSelect: 'none',
                  pointerEvents: 'none'
                }}>
                  Create API Key
                </Text>
              </XStack>
            </Pressable>
          </XStack>
        </ScrollView>
      </View>
    </View>
  );
};

// Webhook Creator Modal Component
interface WebhookCreatorProps {
  theme: boolean;
  onCreateWebhook: (request: CreateWebhookRequest) => void;
  availableEvents: string[];
  isVisible: boolean;
  onClose: () => void;
}

const WebhookCreator: React.FC<WebhookCreatorProps> = ({
  theme,
  onCreateWebhook,
  availableEvents,
  isVisible,
  onClose
}) => {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [description, setDescription] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);

  // Group events by category
  const groupedEvents = availableEvents.reduce((acc, event) => {
    const category = event.split('.')[0]; // e.g., 'user.created' -> 'user'
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(event);
    return acc;
  }, {} as Record<string, string[]>);

  const handleEventToggle = (event: string) => {
    setSelectedEvents(prev =>
      prev.includes(event)
        ? prev.filter(e => e !== event)
        : [...prev, event]
    );
  };

  const handleSelectAll = () => {
    setSelectedEvents(availableEvents);
  };

  const handleClearAll = () => {
    setSelectedEvents([]);
  };

  const handleSelectCategory = (category: string) => {
    const categoryEvents = groupedEvents[category];
    setSelectedEvents(prev => {
      const newEvents = [...prev];
      categoryEvents.forEach(event => {
        if (!newEvents.includes(event)) {
          newEvents.push(event);
        }
      });
      return newEvents;
    });
  };

  const handleClearCategory = (category: string) => {
    const categoryEvents = groupedEvents[category];
    setSelectedEvents(prev => prev.filter(event => !categoryEvents.includes(event)));
  };

  const handleSubmit = () => {
    if (!webhookUrl.trim()) {
      Alert.alert('Error', 'Please enter a webhook URL');
      return;
    }

    if (selectedEvents.length === 0) {
      Alert.alert('Error', 'Please select at least one event');
      return;
    }

    // Validate URL
    try {
      new URL(webhookUrl);
    } catch {
      Alert.alert('Error', 'Please enter a valid URL');
      return;
    }

    onCreateWebhook({
      url: webhookUrl.trim(),
      events: selectedEvents,
      description: description.trim() || undefined,
      isActive
    });

    // Reset form
    setWebhookUrl('');
    setDescription('');
    setSelectedEvents([]);
    setIsActive(true);
  };

  if (!isVisible) return null;

  return (
    <View style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <View style={[
        getModernCardStyle(theme, 'large'),
        {
          width: '90%',
          maxWidth: 600,
          maxHeight: '90%',
          padding: 24
        }
      ]}>
        {/* Header */}
        <XStack justifyContent="space-between" alignItems="center" marginBottom={24}>
          <YStack>
            <Text style={{
              color: getModernTextColor(theme),
              fontSize: 20,
              fontWeight: '700',
              marginBottom: 4
            }}>
              Create New Webhook
            </Text>
            <Text style={{
              color: getModernTextColor(theme, 'muted'),
              fontSize: 14
            }}>
              Configure a webhook to receive real-time notifications
            </Text>
          </YStack>

          <Pressable
            onPress={onClose}
            style={{
              padding: 8,
              borderRadius: 8,
              backgroundColor: theme ? '#f3f4f6' : '#374151'
            }}
          >
            <MaterialIcons name="close" size={20} color={getModernTextColor(theme, 'muted')} />
          </Pressable>
        </XStack>

        {/* Basic Information */}
        <YStack gap={16} marginBottom={24}>
          <YStack gap={4}>
            <Text style={{
              fontSize: 12,
              fontWeight: '600',
              color: getModernTextColor(theme, 'secondary'),
              textTransform: 'uppercase',
              letterSpacing: 0.5
            }}>
              Webhook URL *
            </Text>
            <Input
              value={webhookUrl}
              onChangeText={setWebhookUrl}
              placeholder="https://your-domain.com/webhook"
              placeholderTextColor={getModernTextColor(theme, 'muted')}
              style={{
                backgroundColor: theme ? '#f8fafc' : '#1f2937',
                borderColor: theme ? '#e2e8f0' : '#374151',
                color: getModernTextColor(theme),
                fontSize: 14
              }}
            />
          </YStack>

          <YStack gap={4}>
            <Text style={{
              fontSize: 12,
              fontWeight: '600',
              color: getModernTextColor(theme, 'secondary'),
              textTransform: 'uppercase',
              letterSpacing: 0.5
            }}>
              Description
            </Text>
            <Input
              value={description}
              onChangeText={setDescription}
              placeholder="Optional description for this webhook"
              placeholderTextColor={getModernTextColor(theme, 'muted')}
              style={{
                backgroundColor: theme ? '#f8fafc' : '#1f2937',
                borderColor: theme ? '#e2e8f0' : '#374151',
                color: getModernTextColor(theme),
                fontSize: 14
              }}
            />
          </YStack>

          <XStack alignItems="center" gap={12}>
            <Switch
              value={isActive}
              onValueChange={setIsActive}
              size="$3"
            />
            <Text style={{
              color: getModernTextColor(theme),
              fontSize: 14,
              fontWeight: '500'
            }}>
              Active by default
            </Text>
          </XStack>
        </YStack>

        {/* Events Selection */}
        <YStack gap={16} marginBottom={24}>
          <XStack justifyContent="space-between" alignItems="center">
            <Text style={{
              fontSize: 16,
              fontWeight: '600',
              color: getModernTextColor(theme)
            }}>
              Events ({selectedEvents.length} selected)
            </Text>

            <XStack gap={8}>
              <Pressable
                onPress={handleClearAll}
                style={{
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  backgroundColor: theme ? '#f3f4f6' : '#374151',
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: theme ? '#d1d5db' : '#4b5563'
                }}
              >
                <Text style={{
                  fontSize: 12,
                  fontWeight: '600',
                  color: getModernTextColor(theme, 'muted')
                }}>
                  Clear All
                </Text>
              </Pressable>

              <Pressable
                onPress={handleSelectAll}
                style={{
                  ...getHeroButtonStyle(theme, 'solid'),
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 8
                }}
              >
                <Text style={{
                  fontSize: 12,
                  fontWeight: '600',
                  color: HERO_TEXT_COLOR
                }}>
                  Select All
                </Text>
              </Pressable>
            </XStack>
          </XStack>

          <ScrollView style={{ maxHeight: 300 }}>
            <YStack gap={16}>
              {Object.entries(groupedEvents).map(([category, events]) => {
                const categorySelectedCount = events.filter(e => selectedEvents.includes(e)).length;
                const isCategoryFullySelected = categorySelectedCount === events.length;

                return (
                  <View key={category}>
                    <XStack justifyContent="space-between" alignItems="center" marginBottom={8}>
                      <Text style={{
                        fontSize: 14,
                        fontWeight: '600',
                        color: getModernTextColor(theme, 'secondary')
                      }}>
                        {category.charAt(0).toUpperCase() + category.slice(1)} ({categorySelectedCount}/{events.length})
                      </Text>

                      <XStack gap={4}>
                        <Pressable
                          onPress={() => handleClearCategory(category)}
                          style={{
                            paddingHorizontal: 8,
                            paddingVertical: 4,
                            backgroundColor: theme ? '#fef2f2' : '#7f1d1d',
                            borderRadius: 6,
                            borderWidth: 1,
                            borderColor: theme ? '#fecaca' : '#dc2626'
                          }}
                        >
                          <Text style={{
                            fontSize: 10,
                            fontWeight: '600',
                            color: '#dc2626'
                          }}>
                            Clear
                          </Text>
                        </Pressable>

                        <Pressable
                          onPress={() => handleSelectCategory(category)}
                          style={{
                            paddingHorizontal: 8,
                            paddingVertical: 4,
                            backgroundColor: isCategoryFullySelected ? '#dcfce7' : '#3b82f6',
                            borderRadius: 6,
                            borderWidth: 1,
                            borderColor: isCategoryFullySelected ? '#bbf7d0' : '#2563eb'
                          }}
                        >
                          <Text style={{
                            fontSize: 10,
                            fontWeight: '600',
                            color: isCategoryFullySelected ? '#16a34a' : 'white'
                          }}>
                            {isCategoryFullySelected ? 'All' : 'Select'}
                          </Text>
                        </Pressable>
                      </XStack>
                    </XStack>

                    <YStack gap={8} marginBottom={16}>
                      {events.map((event) => (
                        <Pressable
                          key={event}
                          onPress={() => handleEventToggle(event)}
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            padding: 12,
                            backgroundColor: selectedEvents.includes(event)
                              ? '#3b82f620'
                              : theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                            borderRadius: 12,
                            borderWidth: 1,
                            borderColor: selectedEvents.includes(event)
                              ? '#3b82f640'
                              : theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'
                          }}
                        >
                          <View style={{
                            width: 20,
                            height: 20,
                            borderRadius: 4,
                            backgroundColor: selectedEvents.includes(event)
                              ? '#3b82f6'
                              : 'transparent',
                            borderWidth: 2,
                            borderColor: '#3b82f6',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: 12
                          }}>
                            {selectedEvents.includes(event) && (
                              <MaterialIcons name="check" size={12} color="white" />
                            )}
                          </View>

                          <Text style={{
                            fontSize: 14,
                            fontWeight: '500',
                            color: getModernTextColor(theme),
                            flex: 1
                          }}>
                            {event}
                          </Text>
                        </Pressable>
                      ))}
                    </YStack>
                  </View>
                );
              })}
            </YStack>
          </ScrollView>
        </YStack>

        {/* Actions */}
        <XStack gap={12} justifyContent="flex-end">
          <Pressable
            onPress={onClose}
            style={{
              ...getHeroButtonStyle(theme, 'outline'),
              paddingVertical: 12,
              paddingHorizontal: 24,
              borderRadius: 12,
            }}
          >
            <Text style={{
              color: getModernTextColor(theme),
              fontSize: 14,
              fontWeight: '600'
            }}>
              Cancel
            </Text>
          </Pressable>

          <Pressable
            onPress={handleSubmit}
            style={{
              ...getHeroButtonStyle(theme, 'solid'),
              paddingVertical: 12,
              paddingHorizontal: 24,
              borderRadius: 12,
            }}
          >
            <Text style={{
              color: HERO_TEXT_COLOR,
              fontSize: 14,
              fontWeight: '600'
            }}>
              Create Webhook
            </Text>
          </Pressable>
        </XStack>
      </View>
    </View>
  );
};

// Backend API Key Management Component
interface BackendAPIKeyManagerProps {
  apiKeys: BackendAPIKey[];
  theme: boolean;
  onDeleteKey: (keyId: string) => void;
  onToggleKey: (keyId: string) => void;
  onRegenerateKey: (keyId: string) => void;
  onCreateNew: () => void;
}

const BackendAPIKeyManager: React.FC<BackendAPIKeyManagerProps> = ({ 
  apiKeys, 
  theme, 
  onDeleteKey, 
  onToggleKey, 
  onRegenerateKey,
  onCreateNew 
}) => {
  const copyToClipboard = (text: string, type: string) => {
    if (Platform.OS === 'web') {
      navigator.clipboard.writeText(text);
    } else {
      Clipboard.setString(text);
    }
    Alert.alert('Copied', `${type} copied to clipboard`);
  };

  const isExpired = (expiresAt?: number) => {
    if (!expiresAt) return false;
    return Date.now() > expiresAt;
  };

  const formatUsage = (usage: BackendAPIKey['usage']) => {
    const dailyPercent = (usage.totalRequests / usage.dailyLimit) * 100;
    const monthlyPercent = (usage.totalRequests / usage.monthlyLimit) * 100;
    return { dailyPercent, monthlyPercent };
  };

  return (
    <YStack gap={12}>
      <XStack alignItems="center" justifyContent="space-between">
        <H3 style={{
          color: getModernTextColor(theme),
          userSelect: 'none',
          pointerEvents: 'none'
        }}>
          Backend API Keys
        </H3>
        <XStack alignItems="center" gap={12}>
          <View style={{
            backgroundColor: theme ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.2)',
            borderRadius: 12,
            paddingHorizontal: 12,
            paddingVertical: 6
          }}>
            <Text style={{
              color: '#3b82f6',
              fontSize: 12,
              fontWeight: '600',
              userSelect: 'none',
              pointerEvents: 'none'
            }}>
              {apiKeys.filter(key => key.isActive).length} Active
            </Text>
          </View>
          
          <Pressable
            onPress={onCreateNew}
            style={[
              getHeroButtonStyle(theme, 'solid'),
              {
                paddingHorizontal: 20,
                paddingVertical: 10,
                borderRadius: 12,
                minWidth: 120,
                alignItems: 'center',
                justifyContent: 'center'
              }
            ]}
          >
            <XStack alignItems="center" gap={6}>
              <MaterialIcons name="add" size={16} color={HERO_TEXT_COLOR} />
              <Text style={{
                color: HERO_TEXT_COLOR,
                fontSize: 14,
                fontWeight: '600',
                userSelect: 'none',
                pointerEvents: 'none'
              }}>
                Create New
              </Text>
            </XStack>
          </Pressable>
        </XStack>
      </XStack>

      <Paragraph
        style={{
          color: getModernTextColor(theme, 'muted'),
          marginBottom: 12,
          lineHeight: 20,
          userSelect: 'none',
          pointerEvents: 'none'
        }}
      >
        Manage API keys for your backend service. These keys provide programmatic access to your SmartSaaS backend with specific permissions and usage limits.
      </Paragraph>

      {apiKeys.length === 0 ? (
        <View style={[
          getModernCardStyle(theme, 'medium'),
          { alignItems: 'center', paddingVertical: 40 }
        ]}>
          <MaterialIcons
            name="vpn-key"
            size={48}
            color={getModernTextColor(theme, 'muted')}
            style={{ marginBottom: 16 }}
          />
          <Text style={{
            color: getModernTextColor(theme, 'muted'),
            fontSize: 16,
            fontWeight: '500',
            textAlign: 'center',
            userSelect: 'none',
            pointerEvents: 'none'
          }}>
            No API keys created yet
          </Text>
          <Text style={{
            color: getModernTextColor(theme, 'muted'),
            fontSize: 14,
            textAlign: 'center',
            marginTop: 4,
            marginBottom: 16,
            userSelect: 'none',
            pointerEvents: 'none'
          }}>
            Create your first API key to access backend services
          </Text>
          
          <Pressable
            onPress={onCreateNew}
            style={[
              getHeroButtonStyle(theme, 'solid'),
              {
                paddingHorizontal: 24,
                paddingVertical: 14,
                borderRadius: 12,
                minWidth: 260,
                alignItems: 'center',
                justifyContent: 'center'
              }
            ]}
          >
            <XStack alignItems="center" gap={8}>
              <MaterialIcons name="add" size={18} color={HERO_TEXT_COLOR} />
              <Text style={{
                color: HERO_TEXT_COLOR,
                fontSize: 16,
                fontWeight: '600',
                userSelect: 'none',
                pointerEvents: 'none'
              }}>
                Create First API Key
              </Text>
            </XStack>
          </Pressable>
        </View>
      ) : (
        apiKeys.map((apiKey) => {
          const expired = isExpired(apiKey.expiresAt);
          const { dailyPercent, monthlyPercent } = formatUsage(apiKey.usage);
          const expiryDate = apiKey.expiresAt ? new Date(apiKey.expiresAt) : null;
          const lastUsedDate = apiKey.lastUsed ? new Date(apiKey.lastUsed) : null;
          
          return (
            <View
              key={apiKey.id}
              style={[
                getModernCardStyle(theme, 'medium'),
                { 
                  borderLeftWidth: 4,
                  borderLeftColor: !apiKey.isActive ? '#6b7280' : expired ? '#ef4444' : '#22c55e',
                  opacity: apiKey.isActive ? 1 : 0.7
                }
              ]}
            >
              {/* Header */}
              <XStack alignItems="center" justifyContent="space-between" marginBottom={16}>
                <YStack flex={1}>
                  <Text style={{
                    color: getModernTextColor(theme),
                    fontSize: 16,
                    fontWeight: '600',
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}>
                    {apiKey.name}
                  </Text>
                  <XStack alignItems="center" gap={8} marginTop={4}>
                    <Text style={{
                      color: !apiKey.isActive ? '#6b7280' : expired ? '#ef4444' : '#22c55e',
                      fontSize: 12,
                      fontWeight: '500',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}>
                      {!apiKey.isActive ? 'Disabled' : expired ? 'Expired' : 'Active'}
                    </Text>
                    {lastUsedDate && (
                      <>
                        <Text style={{
                          color: getModernTextColor(theme, 'muted'),
                          fontSize: 12,
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }}>
                          •
                        </Text>
                        <Text style={{
                          color: getModernTextColor(theme, 'muted'),
                          fontSize: 12,
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }}>
                          Last used: {lastUsedDate.toLocaleDateString()}
                        </Text>
                      </>
                    )}
                  </XStack>
                </YStack>
                
                <XStack alignItems="center" gap={8}>
                  <View style={{
                    backgroundColor: !apiKey.isActive ? 'rgba(107,114,128,0.1)' : expired ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
                    borderRadius: 8,
                    paddingHorizontal: 8,
                    paddingVertical: 4
                  }}>
                    <Text style={{
                      color: !apiKey.isActive ? '#6b7280' : expired ? '#ef4444' : '#22c55e',
                      fontSize: 10,
                      fontWeight: '600',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}>
                      {!apiKey.isActive ? 'DISABLED' : expired ? 'EXPIRED' : 'ACTIVE'}
                    </Text>
                  </View>

                  <Pressable
                    onPress={() => onToggleKey(apiKey.id)}
                    style={[
                      getModernActionStyle(theme, apiKey.isActive ? 'amber' : 'green')
                    ]}
                  >
                    <MaterialIcons 
                      name={apiKey.isActive ? 'pause' : 'play-arrow'} 
                      size={16} 
                      color={apiKey.isActive ? '#f59e0b' : '#22c55e'} 
                    />
                  </Pressable>
                </XStack>
              </XStack>

              {/* API Key Preview */}
              <View style={{
                backgroundColor: theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                borderRadius: 12,
                padding: 12,
                marginBottom: 16
              }}>
                <XStack alignItems="center" justifyContent="space-between">
                  <YStack flex={1}>
                    <Text style={{
                      fontSize: 12,
                      fontWeight: '600',
                      color: getModernTextColor(theme, 'secondary'),
                      marginBottom: 4,
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}>
                      API KEY
                    </Text>
                    <Text
                      numberOfLines={1}
                      style={{
                        color: getModernTextColor(theme),
                        fontSize: 14,
                        fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
                        userSelect: 'none',
                        pointerEvents: 'none'
                      }}
                    >
                      {apiKey.keyPreview}
                    </Text>
                  </YStack>
                  
                  <Pressable
                    onPress={() => copyToClipboard(apiKey.fullKey || apiKey.keyPreview, 'API Key')}
                    style={[
                      getModernActionStyle(theme, 'blue'),
                      { marginLeft: 12 }
                    ]}
                  >
                    <MaterialIcons name="content-copy" size={16} color="#3b82f6" />
                  </Pressable>
                </XStack>
              </View>

              {/* Permissions */}
              <View style={{ marginBottom: 16 }}>
                <Text style={{
                  fontSize: 12,
                  fontWeight: '600',
                  color: getModernTextColor(theme, 'secondary'),
                  marginBottom: 8,
                  userSelect: 'none',
                  pointerEvents: 'none'
                }}>
                  PERMISSIONS ({apiKey.permissions.length})
                </Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {apiKey.permissions.slice(0, 6).map((permission, index) => {
                    const permissionData = BACKEND_API_PERMISSIONS.find(p => p.id === permission);
                    return (
                      <View
                        key={index}
                        style={{
                          backgroundColor: (permissionData?.color || '#6b7280') + '20',
                          borderRadius: 6,
                          paddingHorizontal: 8,
                          paddingVertical: 3
                        }}
                      >
                        <Text style={{
                          color: permissionData?.color || '#6b7280',
                          fontSize: 11,
                          fontWeight: '500',
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }}>
                          {permissionData?.name || permission}
                        </Text>
                      </View>
                    );
                  })}
                  {apiKey.permissions.length > 6 && (
                    <View style={{
                      backgroundColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                      borderRadius: 6,
                      paddingHorizontal: 8,
                      paddingVertical: 3
                    }}>
                      <Text style={{
                        color: getModernTextColor(theme, 'muted'),
                        fontSize: 11,
                        fontWeight: '500',
                        userSelect: 'none',
                        pointerEvents: 'none'
                      }}>
                        +{apiKey.permissions.length - 6} more
                      </Text>
                    </View>
                  )}
                </View>
              </View>

              {/* Usage Statistics */}
              <View style={{ marginBottom: 16 }}>
                <Text style={{
                  fontSize: 12,
                  fontWeight: '600',
                  color: getModernTextColor(theme, 'secondary'),
                  marginBottom: 8,
                  userSelect: 'none',
                  pointerEvents: 'none'
                }}>
                  USAGE & LIMITS
                </Text>
                
                <XStack gap={12}>
                  <YStack flex={1}>
                    <XStack alignItems="center" justifyContent="space-between" marginBottom={4}>
                      <Text style={{
                        fontSize: 11,
                        color: getModernTextColor(theme, 'muted'),
                        userSelect: 'none',
                        pointerEvents: 'none'
                      }}>
                        Daily
                      </Text>
                      <Text style={{
                        fontSize: 11,
                        fontWeight: '600',
                        color: dailyPercent > 80 ? '#ef4444' : dailyPercent > 60 ? '#f59e0b' : '#22c55e',
                        userSelect: 'none',
                        pointerEvents: 'none'
                      }}>
                        {Math.round(dailyPercent)}%
                      </Text>
                    </XStack>
                    <View style={{
                      height: 4,
                      backgroundColor: theme ? '#f1f5f9' : '#334155',
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}>
                      <View style={{
                        height: '100%',
                        width: `${Math.min(dailyPercent, 100)}%`,
                        backgroundColor: dailyPercent > 80 ? '#ef4444' : dailyPercent > 60 ? '#f59e0b' : '#22c55e',
                        borderRadius: 2
                      }} />
                    </View>
                  </YStack>

                  <YStack flex={1}>
                    <XStack alignItems="center" justifyContent="space-between" marginBottom={4}>
                      <Text style={{
                        fontSize: 11,
                        color: getModernTextColor(theme, 'muted'),
                        userSelect: 'none',
                        pointerEvents: 'none'
                      }}>
                        Monthly
                      </Text>
                      <Text style={{
                        fontSize: 11,
                        fontWeight: '600',
                        color: monthlyPercent > 80 ? '#ef4444' : monthlyPercent > 60 ? '#f59e0b' : '#22c55e',
                        userSelect: 'none',
                        pointerEvents: 'none'
                      }}>
                        {Math.round(monthlyPercent)}%
                      </Text>
                    </XStack>
                    <View style={{
                      height: 4,
                      backgroundColor: theme ? '#f1f5f9' : '#334155',
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}>
                      <View style={{
                        height: '100%',
                        width: `${Math.min(monthlyPercent, 100)}%`,
                        backgroundColor: monthlyPercent > 80 ? '#ef4444' : monthlyPercent > 60 ? '#f59e0b' : '#22c55e',
                        borderRadius: 2
                      }} />
                    </View>
                  </YStack>
                </XStack>

                <XStack alignItems="center" justifyContent="space-between" marginTop={8}>
                  <Text style={{
                    fontSize: 11,
                    color: getModernTextColor(theme, 'muted'),
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}>
                    Total Requests: {apiKey.usage.totalRequests.toLocaleString()}
                  </Text>
                  {expiryDate && (
                    <Text style={{
                      fontSize: 11,
                      color: expired ? '#ef4444' : getModernTextColor(theme, 'muted'),
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}>
                      Expires: {expiryDate.toLocaleDateString()}
                    </Text>
                  )}
                </XStack>
              </View>

              {/* Action Buttons */}
              <XStack gap={8} justifyContent="flex-end">
                <Pressable
                  onPress={() => onRegenerateKey(apiKey.id)}
                  style={[
                    getModernActionStyle(theme, 'amber'),
                    {
                      paddingHorizontal: 16,
                      paddingVertical: 8,
                      borderRadius: 10,
                      minWidth: 100,
                      alignItems: 'center',
                      justifyContent: 'center'
                    }
                  ]}
                >
                  <XStack alignItems="center" gap={4}>
                    <MaterialIcons name="refresh" size={14} color="#f59e0b" />
                    <Text style={{
                      color: '#f59e0b',
                      fontSize: 12,
                      fontWeight: '600',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}>
                      Regenerate
                    </Text>
                  </XStack>
                </Pressable>
                
                <Pressable
                  onPress={() => {
                    Alert.alert(
                      'Delete API Key',
                      `Permanently delete "${apiKey.name}"? This action cannot be undone.`,
                      [
                        { text: 'Cancel', style: 'cancel' },
                        { text: 'Delete', style: 'destructive', onPress: () => onDeleteKey(apiKey.id) }
                      ]
                    );
                  }}
                  style={[
                    getModernActionStyle(theme, 'red'),
                    {
                      paddingHorizontal: 16,
                      paddingVertical: 8,
                      borderRadius: 10,
                      minWidth: 80,
                      alignItems: 'center',
                      justifyContent: 'center'
                    }
                  ]}
                >
                  <XStack alignItems="center" gap={4}>
                    <MaterialIcons name="delete" size={14} color="#ef4444" />
                    <Text style={{
                      color: '#ef4444',
                      fontSize: 12,
                      fontWeight: '600',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}>
                      Delete
                    </Text>
                  </XStack>
                </Pressable>
              </XStack>
            </View>
          );
        })
      )}
    </YStack>
  );
};

// Webhook Management Component
interface WebhookManagerProps {
  webhooks: WebhookInfo[];
  theme: any;
  onDeleteWebhook: (webhookId: string) => void;
  onToggleWebhook: (webhookId: string) => void;
  onTestWebhook: (webhookId: string) => void;
  onRegenerateSecret: (webhookId: string) => void;
  onCreateNew: () => void;
}

const WebhookManager: React.FC<WebhookManagerProps> = ({
  webhooks,
  theme,
  onDeleteWebhook,
  onToggleWebhook,
  onTestWebhook,
  onRegenerateSecret,
  onCreateNew
}) => {
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (webhook: WebhookInfo) => {
    if (!webhook.isActive) return '#6b7280';
    if (webhook.stats.successRate >= 90) return '#22c55e';
    if (webhook.stats.successRate >= 70) return '#f59e0b';
    return '#ef4444';
  };

  const getStatusText = (webhook: WebhookInfo) => {
    if (!webhook.isActive) return 'Disabled';
    if (webhook.stats.totalRequests === 0) return 'Never Used';
    return `${webhook.stats.successRate}% Success`;
  };

  return (
    <YStack>
      {/* Header */}
      <View style={[
        getModernCardStyle(theme, 'medium'),
        { marginBottom: 16 }
      ]}>
        <XStack justifyContent="space-between" alignItems="center" marginBottom={16}>
          <YStack>
            <Text style={{
              color: getModernTextColor(theme),
              fontSize: 20,
              fontWeight: '700',
              marginBottom: 4
            }}>
              Webhook Management
            </Text>
            <Text style={{
              color: getModernTextColor(theme, 'muted'),
              fontSize: 14
            }}>
              Configure custom webhook URLs to receive real-time notifications
            </Text>
          </YStack>

          <Pressable
            onPress={onCreateNew}
            style={[
              getHeroButtonStyle(theme, 'solid'),
              {
                flexDirection: 'row',
                alignItems: 'center',
                paddingVertical: 12,
                paddingHorizontal: 20,
                borderRadius: 12,
                minWidth: 260
              }
            ]}
          >
            <MaterialIcons name="add" size={20} color={HERO_TEXT_COLOR} style={{ marginRight: 8 }} />
            <Text style={{
              color: HERO_TEXT_COLOR,
              fontSize: 14,
              fontWeight: '600'
            }}>
              Create Webhook
            </Text>
          </Pressable>
        </XStack>

        {/* Stats */}
        <XStack gap={16}>
          <View style={{
            flex: 1,
            padding: 16,
            backgroundColor: theme ? '#f8fafc' : '#1f2937',
            borderRadius: 12,
            borderWidth: 1,
            borderColor: theme ? '#e2e8f0' : '#374151'
          }}>
            <Text style={{
              color: getModernTextColor(theme),
              fontSize: 24,
              fontWeight: '700',
              marginBottom: 4
            }}>
              {webhooks.length}
            </Text>
            <Text style={{
              color: getModernTextColor(theme, 'muted'),
              fontSize: 12,
              fontWeight: '500'
            }}>
              Total Webhooks
            </Text>
          </View>

          <View style={{
            flex: 1,
            padding: 16,
            backgroundColor: theme ? '#f8fafc' : '#1f2937',
            borderRadius: 12,
            borderWidth: 1,
            borderColor: theme ? '#e2e8f0' : '#374151'
          }}>
            <Text style={{
              color: getModernTextColor(theme),
              fontSize: 24,
              fontWeight: '700',
              marginBottom: 4
            }}>
              {webhooks.filter(w => w.isActive).length}
            </Text>
            <Text style={{
              color: getModernTextColor(theme, 'muted'),
              fontSize: 12,
              fontWeight: '500'
            }}>
              Active
            </Text>
          </View>

          <View style={{
            flex: 1,
            padding: 16,
            backgroundColor: theme ? '#f8fafc' : '#1f2937',
            borderRadius: 12,
            borderWidth: 1,
            borderColor: theme ? '#e2e8f0' : '#374151'
          }}>
            <Text style={{
              color: getModernTextColor(theme),
              fontSize: 24,
              fontWeight: '700',
              marginBottom: 4
            }}>
              {webhooks.reduce((sum, w) => sum + w.stats.totalRequests, 0)}
            </Text>
            <Text style={{
              color: getModernTextColor(theme, 'muted'),
              fontSize: 12,
              fontWeight: '500'
            }}>
              Total Requests
            </Text>
          </View>
        </XStack>
      </View>

      {/* Webhook List */}
      {webhooks.length === 0 ? (
        <View style={[
          getModernCardStyle(theme, 'medium'),
          {
            padding: 40,
            alignItems: 'center',
            justifyContent: 'center'
          }
        ]}>
          <MaterialIcons
            name="webhook"
            size={48}
            color={getModernTextColor(theme, 'muted')}
            style={{ marginBottom: 16 }}
          />
          <Text style={{
            color: getModernTextColor(theme),
            fontSize: 18,
            fontWeight: '600',
            marginBottom: 8,
            textAlign: 'center'
          }}>
            No Webhooks Created
          </Text>
          <Text style={{
            color: getModernTextColor(theme, 'muted'),
            fontSize: 14,
            textAlign: 'center',
            marginBottom: 20,
            lineHeight: 20
          }}>
            Create your first webhook to start receiving real-time notifications about events in your SmartSaaS account.
          </Text>
          <Pressable
            onPress={onCreateNew}
            style={[
              getHeroButtonStyle(theme, 'solid'),
              {
                flexDirection: 'row',
                alignItems: 'center',
                paddingVertical: 12,
                paddingHorizontal: 24,
                borderRadius: 12,
                minWidth:300
              }
            ]}
          >
            <MaterialIcons name="add" size={20} color={HERO_TEXT_COLOR} style={{ marginRight: 8 }} />
            <Text style={{
              color: HERO_TEXT_COLOR,
              fontSize: 16,
              fontWeight: '600'
            }}>
              Create Your First Webhook
            </Text>
          </Pressable>
        </View>
      ) : (
        <YStack gap={12}>
          {webhooks.map((webhook) => (
            <View
              key={webhook.id}
              style={[
                getModernCardStyle(theme, 'small'),
                {
                  padding: 16,
                  borderLeftWidth: 4,
                  borderLeftColor: getStatusColor(webhook)
                }
              ]}
            >
              <XStack justifyContent="space-between" alignItems="flex-start" marginBottom={12}>
                <YStack flex={1}>
                  <Text style={{
                    color: getModernTextColor(theme),
                    fontSize: 16,
                    fontWeight: '600',
                    marginBottom: 4
                  }}>
                    {webhook.url}
                  </Text>
                  {webhook.description && (
                    <Text style={{
                      color: getModernTextColor(theme, 'muted'),
                      fontSize: 14,
                      marginBottom: 8
                    }}>
                      {webhook.description}
                    </Text>
                  )}
                  <XStack gap={16} alignItems="center">
                    <XStack alignItems="center" gap={4}>
                      <View style={{
                        width: 8,
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: getStatusColor(webhook)
                      }} />
                      <Text style={{
                        color: getModernTextColor(theme, 'muted'),
                        fontSize: 12,
                        fontWeight: '500'
                      }}>
                        {getStatusText(webhook)}
                      </Text>
                    </XStack>
                    <Text style={{
                      color: getModernTextColor(theme, 'muted'),
                      fontSize: 12
                    }}>
                      Created {formatDate(webhook.createdAt)}
                    </Text>
                    {webhook.lastTriggered && (
                      <Text style={{
                        color: getModernTextColor(theme, 'muted'),
                        fontSize: 12
                      }}>
                        Last triggered {formatDate(webhook.lastTriggered)}
                      </Text>
                    )}
                  </XStack>
                </YStack>

                <Switch
                  value={webhook.isActive}
                  onValueChange={() => onToggleWebhook(webhook.id)}
                  size="$3"
                />
              </XStack>

              {/* Events */}
              <View style={{
                marginBottom: 12,
                padding: 8,
                backgroundColor: theme ? '#f1f5f9' : '#374151',
                borderRadius: 8
              }}>
                <Text style={{
                  color: getModernTextColor(theme, 'muted'),
                  fontSize: 12,
                  fontWeight: '600',
                  marginBottom: 4
                }}>
                  Events ({webhook.events.length})
                </Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
                  {webhook.events.map((event) => (
                    <View
                      key={event}
                      style={{
                        paddingHorizontal: 8,
                        paddingVertical: 2,
                        backgroundColor: theme ? '#e2e8f0' : '#4b5563',
                        borderRadius: 4
                      }}
                    >
                      <Text style={{
                        color: getModernTextColor(theme),
                        fontSize: 10,
                        fontWeight: '500'
                      }}>
                        {event}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>

              {/* Stats */}
              <XStack justifyContent="space-between" alignItems="center" marginBottom={12}>
                <XStack gap={16}>
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{
                      color: getModernTextColor(theme),
                      fontSize: 16,
                      fontWeight: '600'
                    }}>
                      {webhook.stats.totalRequests}
                    </Text>
                    <Text style={{
                      color: getModernTextColor(theme, 'muted'),
                      fontSize: 10
                    }}>
                      Total
                    </Text>
                  </View>
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{
                      color: '#22c55e',
                      fontSize: 16,
                      fontWeight: '600'
                    }}>
                      {webhook.stats.successfulRequests}
                    </Text>
                    <Text style={{
                      color: getModernTextColor(theme, 'muted'),
                      fontSize: 10
                    }}>
                      Success
                    </Text>
                  </View>
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{
                      color: '#ef4444',
                      fontSize: 16,
                      fontWeight: '600'
                    }}>
                      {webhook.stats.failedRequests}
                    </Text>
                    <Text style={{
                      color: getModernTextColor(theme, 'muted'),
                      fontSize: 10
                    }}>
                      Failed
                    </Text>
                  </View>
                </XStack>
              </XStack>

              {/* Actions */}
              <XStack gap={8}>
                <Pressable
                  onPress={() => onTestWebhook(webhook.id)}
                  style={{
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingVertical: 8,
                    paddingHorizontal: 12,
                    backgroundColor: theme ? '#f0f9ff' : '#1e3a8a',
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: theme ? '#bae6fd' : '#3b82f6'
                  }}
                >
                  <MaterialIcons name="play-arrow" size={16} color="#3b82f6" style={{ marginRight: 4 }} />
                  <Text style={{
                    color: '#3b82f6',
                    fontSize: 12,
                    fontWeight: '600'
                  }}>
                    Test
                  </Text>
                </Pressable>

                <Pressable
                  onPress={() => onRegenerateSecret(webhook.id)}
                  style={{
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingVertical: 8,
                    paddingHorizontal: 12,
                    backgroundColor: theme ? '#fef3c7' : '#92400e',
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: theme ? '#fde68a' : '#f59e0b'
                  }}
                >
                  <MaterialIcons name="refresh" size={16} color="#f59e0b" style={{ marginRight: 4 }} />
                  <Text style={{
                    color: '#f59e0b',
                    fontSize: 12,
                    fontWeight: '600'
                  }}>
                    Regenerate
                  </Text>
                </Pressable>

                <Pressable
                  onPress={() => onDeleteWebhook(webhook.id)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingVertical: 8,
                    paddingHorizontal: 12,
                    backgroundColor: theme ? '#fef2f2' : '#991b1b',
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: theme ? '#fecaca' : '#ef4444'
                  }}
                >
                  <MaterialIcons name="delete" size={16} color="#ef4444" style={{ marginRight: 4 }} />
                  <Text style={{
                    color: '#ef4444',
                    fontSize: 12,
                    fontWeight: '600'
                  }}>
                    Delete
                  </Text>
                </Pressable>
              </XStack>
            </View>
          ))}
        </YStack>
      )}
    </YStack>
  );
};

// OAuth Provider Card Component
interface OAuthProviderCardProps {
  provider: typeof OAUTH_PROVIDERS[0];
  config: OAuthConfig;
  onConfigChange: (config: OAuthConfig) => void;
  onConnect: (providerId: string) => void;
  theme: boolean;
  isConnected: boolean;
}

const OAuthProviderCard: React.FC<OAuthProviderCardProps> = ({
  provider,
  config,
  onConfigChange,
  onConnect,
  theme,
  isConnected
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const renderIcon = (size = 20) => {
    const iconProps = { size, color: 'white' };
    switch (provider.iconLibrary) {
      case 'FontAwesome5':
        return <FontAwesome5 name={provider.icon as any} {...iconProps} />;
      case 'Ionicons':
        return <Ionicons name={provider.icon as any} {...iconProps} />;
      default:
        return <MaterialIcons name={provider.icon as any} {...iconProps} />;
    }
  };

  const handleConnect = () => {
    if (!config.clientId || !config.clientSecret) {
      Alert.alert('Missing Configuration', 'Please provide Client ID and Client Secret first.');
      return;
    }

    Alert.alert(
      'Initiate OAuth Flow',
      `Start OAuth authentication with ${provider.name}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Connect', onPress: () => onConnect(provider.id) }
      ]
    );
  };

  const handleDisconnect = () => {
    Alert.alert(
      'Disconnect OAuth',
      `Remove ${provider.name} integration? This will revoke all tokens.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Disconnect', style: 'destructive', onPress: () => console.log(`Disconnecting ${provider.name}...`) }
      ]
    );
  };

  return (
    <Animatable.View
      animation="fadeInUp"
      delay={100}
      style={[
        getModernCardStyle(theme, 'medium'),
        { marginVertical: 8, overflow: 'hidden' }
      ]}
    >
      {/* Header */}
      <Pressable
        onPress={() => setIsExpanded(!isExpanded)}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: isExpanded ? 16 : 0,
          userSelect: 'none'
        }}
      >
        <XStack alignItems="center" gap={12}>
          <LinearGradient
            colors={provider.gradientColors as [string, string, ...string[]]}
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {renderIcon(20)}
          </LinearGradient>
          <YStack>
            <SizableText
              size="$4"
              fontWeight="600"
              style={{
                color: getModernTextColor(theme),
                userSelect: 'none',
                pointerEvents: 'none'
              }}
            >
              {provider.name}
            </SizableText>
            <SizableText
              size="$2"
              style={{
                color: getModernTextColor(theme, 'muted'),
                userSelect: 'none',
                pointerEvents: 'none'
              }}
            >
              {isConnected ? 'Connected' : 'Not configured'}
            </SizableText>
          </YStack>
        </XStack>

        <XStack alignItems="center" gap={8}>
          {isConnected && (
            <View style={{
              backgroundColor: '#22c55e',
              borderRadius: 6,
              paddingHorizontal: 8,
              paddingVertical: 4
            }}>
              <Text style={{
                color: 'white',
                fontSize: 10,
                fontWeight: '600',
                userSelect: 'none',
                pointerEvents: 'none'
              }}>
                ACTIVE
              </Text>
            </View>
          )}
          <MaterialIcons
            name={isExpanded ? "keyboard-arrow-up" : "keyboard-arrow-down"}
            size={24}
            color={getModernTextColor(theme, 'muted')}
          />
        </XStack>
      </Pressable>

      {/* Expanded Configuration */}
      {isExpanded && (
        <Animatable.View animation="slideInDown" duration={300}>
          <YStack gap={16}>
            {/* Configuration Fields */}
            {isEditing ? (
              <YStack gap={12}>
                <YStack gap={4}>
                  <Text style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: getModernTextColor(theme, 'secondary'),
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}>
                    Client ID
                  </Text>
                  <TextInput
                    style={[
                      styles.validationTextInput,
                      {
                        backgroundColor: theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                        borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                        color: getModernTextColor(theme)
                      }
                    ]}
                    value={config.clientId}
                    onChangeText={(text) => onConfigChange({ ...config, clientId: text })}
                    placeholder="Enter client ID..."
                    placeholderTextColor={getModernTextColor(theme, 'muted')}
                    secureTextEntry={false}
                  />
                </YStack>

                <YStack gap={4}>
                  <Text style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: getModernTextColor(theme, 'secondary'),
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}>
                    Client Secret
                  </Text>
                  <TextInput
                    style={[
                      styles.validationTextInput,
                      {
                        backgroundColor: theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                        borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                        color: getModernTextColor(theme)
                      }
                    ]}
                    value={config.clientSecret}
                    onChangeText={(text) => onConfigChange({ ...config, clientSecret: text })}
                    placeholder="Enter client secret..."
                    placeholderTextColor={getModernTextColor(theme, 'muted')}
                    secureTextEntry={true}
                  />
                </YStack>

                <YStack gap={4}>
                  <Text style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: getModernTextColor(theme, 'secondary'),
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}>
                    Redirect URI
                  </Text>
                  <TextInput
                    style={[
                      styles.validationTextInput,
                      {
                        backgroundColor: theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                        borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                        color: getModernTextColor(theme)
                      }
                    ]}
                    value={config.redirectUri}
                    onChangeText={(text) => onConfigChange({ ...config, redirectUri: text })}
                    placeholder="https://your-app.com/oauth/callback"
                    placeholderTextColor={getModernTextColor(theme, 'muted')}
                  />
                </YStack>
              </YStack>
            ) : (
              <YStack gap={8}>
                <View style={{
                  backgroundColor: theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                  borderRadius: 12,
                  padding: 12
                }}>
                  <Text style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: getModernTextColor(theme, 'secondary'),
                    marginBottom: 4,
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}>
                    CLIENT ID
                  </Text>
                  <Text style={{
                    color: getModernTextColor(theme),
                    fontSize: 14,
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}>
                    {config.clientId || 'Not configured'}
                  </Text>
                </View>
                
                <View style={{
                  backgroundColor: theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                  borderRadius: 12,
                  padding: 12
                }}>
                  <Text style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: getModernTextColor(theme, 'secondary'),
                    marginBottom: 4,
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}>
                    REDIRECT URI
                  </Text>
                  <Text style={{
                    color: getModernTextColor(theme),
                    fontSize: 14,
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}>
                    {config.redirectUri || 'Not configured'}
                  </Text>
                </View>
              </YStack>
            )}

            {/* Scopes */}
            <YStack gap={4}>
              <Text style={{
                fontSize: 12,
                fontWeight: '600',
                color: getModernTextColor(theme, 'secondary'),
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                userSelect: 'none',
                pointerEvents: 'none'
              }}>
                Scopes
              </Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {provider.defaultScopes.map((scope, index) => (
                  <View
                    key={index}
                    style={{
                      backgroundColor: provider.color + '20',
                      borderRadius: 8,
                      paddingHorizontal: 8,
                      paddingVertical: 4,
                      borderWidth: 1,
                      borderColor: provider.color + '40'
                    }}
                  >
                    <Text style={{
                      color: provider.color,
                      fontSize: 12,
                      fontWeight: '500',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}>
                      {scope}
                    </Text>
                  </View>
                ))}
              </View>
            </YStack>

            {/* Action Buttons */}
            <XStack gap={12} justifyContent="flex-end">
              {isEditing ? (
                <>
                  <Pressable
                    onPress={() => setIsEditing(false)}
                    style={[
                      getHeroButtonStyle(theme, 'outline'),
                      {
                        paddingHorizontal: 20,
                        paddingVertical: 10,
                        borderRadius: 12,
                        minWidth: 80,
                        alignItems: 'center',
                        justifyContent: 'center'
                      }
                    ]}
                  >
                    <Text style={{
                      color: getModernTextColor(theme),
                      fontSize: 14,
                      fontWeight: '600',
                      userSelect: 'none',
                      pointerEvents: 'none',
                      textAlign: 'center'
                    }}>
                      Cancel
                    </Text>
                  </Pressable>
                  <Pressable
                    onPress={() => setIsEditing(false)}
                    style={[
                      getHeroButtonStyle(theme, 'solid'),
                      {
                        paddingHorizontal: 20,
                        paddingVertical: 10,
                        borderRadius: 12,
                        minWidth: 80,
                        alignItems: 'center',
                        justifyContent: 'center'
                      }
                    ]}
                  >
                    <Text style={{
                      color: HERO_TEXT_COLOR,
                      fontSize: 14,
                      fontWeight: '600',
                      userSelect: 'none',
                      pointerEvents: 'none',
                      textAlign: 'center'
                    }}>
                      Save
                    </Text>
                  </Pressable>
                </>
              ) : (
                <>
                  <Pressable
                    onPress={() => setIsEditing(true)}
                    style={[
                      getHeroButtonStyle(theme, 'outline'),
                      {
                        paddingHorizontal: 20,
                        paddingVertical: 10,
                        borderRadius: 12,
                        minWidth: 110,
                        alignItems: 'center',
                        justifyContent: 'center'
                      }
                    ]}
                  >
                    <XStack alignItems="center" gap={6}>
                        <MaterialIcons name="edit" size={16} color={getModernTextColor(theme)} />
                      <Text style={{
                          color: getModernTextColor(theme),
                        fontSize: 14,
                        fontWeight: '600',
                        userSelect: 'none',
                        pointerEvents: 'none'
                      }}>
                        Configure
                      </Text>
                    </XStack>
                  </Pressable>
                  
                  {isConnected ? (
                    <Pressable
                      onPress={handleDisconnect}
                      style={[
                        getModernActionStyle(theme, 'red'),
                        {
                          paddingHorizontal: 20,
                          paddingVertical: 10,
                          borderRadius: 12,
                          minWidth: 110,
                          alignItems: 'center',
                          justifyContent: 'center'
                        }
                      ]}
                    >
                      <XStack alignItems="center" gap={6}>
                        <MaterialIcons name="link-off" size={16} color="#ef4444" />
                        <Text style={{
                          color: '#ef4444',
                          fontSize: 14,
                          fontWeight: '600',
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }}>
                          Disconnect
                        </Text>
                      </XStack>
                    </Pressable>
                  ) : (
                    <Pressable
                      onPress={handleConnect}
                      style={[
                        getHeroButtonStyle(theme, 'solid'),
                        {
                          paddingHorizontal: 20,
                          paddingVertical: 10,
                          borderRadius: 12,
                          minWidth: 100,
                          alignItems: 'center',
                          justifyContent: 'center'
                        }
                      ]}
                    >
                      <XStack alignItems="center" gap={6}>
                            <MaterialIcons name="link" size={16} color={HERO_TEXT_COLOR} />
                        <Text style={{
                              color: HERO_TEXT_COLOR,
                          fontSize: 14,
                          fontWeight: '600',
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }}>
                          Connect
                        </Text>
                      </XStack>
                    </Pressable>
                  )}
                </>
              )}
            </XStack>
          </YStack>
        </Animatable.View>
      )}
    </Animatable.View>
  );
};

// Token Management Component
interface TokenManagerProps {
  tokens: TokenInfo[];
  theme: boolean;
  onRefreshToken: (index: number) => void;
  onRevokeToken: (index: number) => void;
}

const TokenManager: React.FC<TokenManagerProps> = ({ tokens, theme, onRefreshToken, onRevokeToken }) => {
  const copyToClipboard = (text: string, type: string) => {
    if (Platform.OS === 'web') {
      navigator.clipboard.writeText(text);
    } else {
      Clipboard.setString(text);
    }
    Alert.alert('Copied', `${type} copied to clipboard`);
  };

  const isExpired = (issuedAt: number, expiresIn: number) => {
    return Date.now() > (issuedAt + expiresIn * 1000);
  };

  return (
    <YStack gap={12}>
      <XStack alignItems="center" justifyContent="space-between">
        <H3 style={{
          color: getModernTextColor(theme),
          userSelect: 'none',
          pointerEvents: 'none'
        }}>
          Active Tokens
        </H3>
        <View style={{
          backgroundColor: theme ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.2)',
          borderRadius: 12,
          paddingHorizontal: 12,
          paddingVertical: 6
        }}>
          <Text style={{
            color: '#3b82f6',
            fontSize: 12,
            fontWeight: '600',
            userSelect: 'none',
            pointerEvents: 'none'
          }}>
            {tokens.length} Active
          </Text>
        </View>
      </XStack>

      {tokens.length === 0 ? (
        <View style={[
          getModernCardStyle(theme, 'medium'),
          { alignItems: 'center', paddingVertical: 40 }
        ]}>
          <MaterialIcons
            name="security"
            size={48}
            color={getModernTextColor(theme, 'muted')}
            style={{ marginBottom: 16 }}
          />
          <Text style={{
            color: getModernTextColor(theme, 'muted'),
            fontSize: 16,
            fontWeight: '500',
            textAlign: 'center',
            userSelect: 'none',
            pointerEvents: 'none'
          }}>
            No active tokens yet
          </Text>
          <Text style={{
            color: getModernTextColor(theme, 'muted'),
            fontSize: 14,
            textAlign: 'center',
            marginTop: 4,
            userSelect: 'none',
            pointerEvents: 'none'
          }}>
            Connect OAuth providers to generate tokens
          </Text>
        </View>
      ) : (
        tokens.map((token, index) => {
          const expired = isExpired(token.issuedAt, token.expiresIn);
          const expiryTime = new Date(token.issuedAt + token.expiresIn * 1000);
          
          return (
            <View
              key={index}
              style={[
                getModernCardStyle(theme, 'medium'),
                { 
                  borderLeftWidth: 4,
                  borderLeftColor: expired ? '#ef4444' : '#22c55e'
                }
              ]}
            >
              <XStack alignItems="center" justifyContent="space-between" marginBottom={16}>
                <YStack>
                  <Text style={{
                    color: getModernTextColor(theme),
                    fontSize: 16,
                    fontWeight: '600',
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}>
                    {token.tokenType} Token #{index + 1}
                  </Text>
                  <Text style={{
                    color: expired ? '#ef4444' : '#22c55e',
                    fontSize: 12,
                    fontWeight: '500',
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}>
                    {expired ? 'Expired' : 'Active'} • Expires: {expiryTime.toLocaleDateString()}
                  </Text>
                </YStack>
                
                <View style={{
                  backgroundColor: expired ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
                  borderRadius: 8,
                  paddingHorizontal: 8,
                  paddingVertical: 4
                }}>
                  <Text style={{
                    color: expired ? '#ef4444' : '#22c55e',
                    fontSize: 10,
                    fontWeight: '600',
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}>
                    {expired ? 'EXPIRED' : 'VALID'}
                  </Text>
                </View>
              </XStack>

              {/* Token Preview */}
              <View style={{
                backgroundColor: theme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                borderRadius: 12,
                padding: 12,
                marginBottom: 12
              }}>
                <XStack alignItems="center" justifyContent="space-between">
                  <YStack flex={1}>
                    <Text style={{
                      fontSize: 12,
                      fontWeight: '600',
                      color: getModernTextColor(theme, 'secondary'),
                      marginBottom: 4,
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}>
                      ACCESS TOKEN
                    </Text>
                    <Text
                      numberOfLines={1}
                      style={{
                        color: getModernTextColor(theme),
                        fontSize: 14,
                        fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
                        userSelect: 'none',
                        pointerEvents: 'none'
                      }}
                    >
                      {token.accessToken.substring(0, 32)}...
                    </Text>
                  </YStack>
                  
                  <Pressable
                    onPress={() => copyToClipboard(token.accessToken, 'Access Token')}
                    style={[
                      getModernActionStyle(theme, 'blue'),
                      { marginLeft: 12 }
                    ]}
                  >
                    <MaterialIcons name="content-copy" size={16} color="#3b82f6" />
                  </Pressable>
                </XStack>
              </View>

              {/* Scope Information */}
              {token.scope && (
                <View style={{ marginBottom: 12 }}>
                  <Text style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: getModernTextColor(theme, 'secondary'),
                    marginBottom: 6,
                    userSelect: 'none',
                    pointerEvents: 'none'
                  }}>
                    SCOPES
                  </Text>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                    {token.scope.split(' ').map((scope, scopeIndex) => (
                      <View
                        key={scopeIndex}
                        style={{
                          backgroundColor: '#3b82f6' + '20',
                          borderRadius: 6,
                          paddingHorizontal: 8,
                          paddingVertical: 3
                        }}
                      >
                        <Text style={{
                          color: '#3b82f6',
                          fontSize: 11,
                          fontWeight: '500',
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }}>
                          {scope}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Action Buttons */}
              <XStack gap={8} justifyContent="flex-end">
                <Pressable
                  onPress={() => onRefreshToken(index)}
                  style={[
                    getModernActionStyle(theme, 'amber'),
                    {
                      paddingHorizontal: 16,
                      paddingVertical: 8,
                      borderRadius: 10,
                      minWidth: 90,
                      alignItems: 'center',
                      justifyContent: 'center'
                    }
                  ]}
                >
                  <XStack alignItems="center" gap={4}>
                    <MaterialIcons name="refresh" size={14} color="#f59e0b" />
                    <Text style={{
                      color: '#f59e0b',
                      fontSize: 12,
                      fontWeight: '600',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}>
                      Refresh
                    </Text>
                  </XStack>
                </Pressable>
                
                <Pressable
                  onPress={() => onRevokeToken(index)}
                  style={[
                    getModernActionStyle(theme, 'red'),
                    {
                      paddingHorizontal: 16,
                      paddingVertical: 8,
                      borderRadius: 10,
                      minWidth: 80,
                      alignItems: 'center',
                      justifyContent: 'center'
                    }
                  ]}
                >
                  <XStack alignItems="center" gap={4}>
                    <MaterialIcons name="block" size={14} color="#ef4444" />
                    <Text style={{
                      color: '#ef4444',
                      fontSize: 12,
                      fontWeight: '600',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}>
                      Revoke
                    </Text>
                  </XStack>
                </Pressable>
              </XStack>
            </View>
          );
        })
      )}

    </YStack>
  );
};

// AI Model Key Manager Component
interface AIModelKeyManagerProps {
  aiModelKeys: AIModelKey[];
  theme: boolean;
  onDeleteKey: (keyId: string) => void;
  onToggleKey: (keyId: string) => void;
  onUpdateKey: (keyId: string, updates: { isDefault?: boolean; isActive?: boolean; modelName?: string }) => void;
  onCreateNew: () => void;
}

const AIModelKeyManager: React.FC<AIModelKeyManagerProps> = ({
  aiModelKeys,
  theme,
  onDeleteKey,
  onToggleKey,
  onUpdateKey,
  onCreateNew
}) => {
  const providerIcons: Record<string, string> = {
    openai: 'smart-toy',
    anthropic: 'psychology',
    google: 'cloud',
    ollama: 'memory',
    cohere: 'auto-awesome',
    mistral: 'wind-power'
  };

  const providerColors: Record<string, string> = {
    openai: '#10a37f',
    anthropic: '#d97757',
    google: '#4285f4',
    ollama: '#3b82f6',
    cohere: '#ff6b6b',
    mistral: '#6366f1'
  };

  return (
    <View style={[getModernCardStyle(theme, 'large'), { padding: 24 }]}>
      <XStack alignItems="center" justifyContent="space-between" marginBottom={20}>
        <View>
          <H2 style={{ color: getModernTextColor(theme), marginBottom: 4 }}>
            AI Model API Keys
          </H2>
          <Paragraph style={{ color: getModernTextColor(theme, 'muted'), fontSize: 14 }}>
            Manage your AI model API keys for OpenAI, Anthropic, Google, and more
          </Paragraph>
        </View>
        <Button
          onPress={onCreateNew}
          style={getHeroButtonStyle(theme, 'solid')}
          size="$4"
        >
          <MaterialIcons name="add" size={20} color={HERO_TEXT_COLOR} style={{ marginRight: 6 }} />
          <Text style={{ color: HERO_TEXT_COLOR, fontWeight: '600' }}>Add Key</Text>
        </Button>
      </XStack>

      {aiModelKeys.length === 0 ? (
        <View style={{ padding: 40, alignItems: 'center' }}>
          <MaterialIcons name="key" size={48} color={getModernTextColor(theme, 'muted')} />
          <Text style={{ color: getModernTextColor(theme, 'muted'), marginTop: 16, fontSize: 16 }}>
            No AI model keys configured
          </Text>
          <Text style={{ color: getModernTextColor(theme, 'muted'), marginTop: 8, fontSize: 14, textAlign: 'center' }}>
            Add your first API key to start using custom AI models
          </Text>
        </View>
      ) : (
        <YStack gap={12}>
          {aiModelKeys.map((key) => (
            <View
              key={key.id}
              style={[
                getModernCardStyle(theme, 'small'),
                {
                  padding: 16,
                  borderLeftWidth: 4,
                  borderLeftColor: providerColors[key.provider] || '#6b7280'
                }
              ]}
            >
              <XStack alignItems="center" justifyContent="space-between">
                <XStack alignItems="center" gap={12} flex={1}>
                  <View
                    style={{
                      backgroundColor: `${providerColors[key.provider] || '#6b7280'}20`,
                      borderRadius: 12,
                      padding: 10
                    }}
                  >
                    <MaterialIcons
                      name={providerIcons[key.provider] || 'key'}
                      size={24}
                      color={providerColors[key.provider] || '#6b7280'}
                    />
                  </View>
                  <YStack flex={1}>
                    <XStack alignItems="center" gap={8}>
                      <Text style={{ color: getModernTextColor(theme), fontSize: 16, fontWeight: '600' }}>
                        {key.provider.toUpperCase()} - {key.modelName}
                      </Text>
                      {key.isDefault && (
                        <View style={{ backgroundColor: '#10b981', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 }}>
                          <Text style={{ color: 'white', fontSize: 10, fontWeight: '600' }}>DEFAULT</Text>
                        </View>
                      )}
                      {key.source === 'company' && (
                        <View style={{ backgroundColor: '#3b82f6', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 }}>
                          <Text style={{ color: 'white', fontSize: 10, fontWeight: '600' }}>COMPANY</Text>
                        </View>
                      )}
                    </XStack>
                    <Text style={{ color: getModernTextColor(theme, 'muted'), fontSize: 12, marginTop: 4 }}>
                      {key.keyPreview}
                    </Text>
                  </YStack>
                </XStack>
                <XStack gap={8}>
                  <Pressable
                    onPress={() => onUpdateKey(key.id, { isDefault: !key.isDefault })}
                    style={[
                      getModernActionStyle(theme, key.isDefault ? 'primary' : 'secondary'),
                      { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8 }
                    ]}
                  >
                    <MaterialIcons
                      name={key.isDefault ? 'star' : 'star-border'}
                      size={16}
                      color={key.isDefault ? '#fbbf24' : getModernTextColor(theme)}
                    />
                  </Pressable>
                  <Pressable
                    onPress={() => onToggleKey(key.id)}
                    style={[
                      getModernActionStyle(theme, key.isActive ? 'secondary' : 'primary'),
                      { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8 }
                    ]}
                  >
                    <MaterialIcons
                      name={key.isActive ? 'toggle-on' : 'toggle-off'}
                      size={20}
                      color={key.isActive ? '#10b981' : '#6b7280'}
                    />
                  </Pressable>
                  <Pressable
                    onPress={() => onDeleteKey(key.id)}
                    style={[
                      getModernActionStyle(theme, 'red'),
                      { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8 }
                    ]}
                  >
                    <MaterialIcons name="delete" size={16} color="#ef4444" />
                  </Pressable>
                </XStack>
              </XStack>
            </View>
          ))}
        </YStack>
      )}
    </View>
  );
};

// AI Model Key Creator Component
interface AIModelKeyCreatorProps {
  isVisible: boolean;
  onClose: () => void;
  onCreateKey: (keyData: CreateAIModelKeyRequest) => void;
  theme: boolean;
}

const AIModelKeyCreator: React.FC<AIModelKeyCreatorProps> = ({
  isVisible,
  onClose,
  onCreateKey,
  theme
}) => {
  const [provider, setProvider] = useState('openai');
  const [modelName, setModelName] = useState('');
  const [apiKey, setApiKey] = useState('');

  const providers = [
    { value: 'openai', label: 'OpenAI', models: ['gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo'] },
    { value: 'anthropic', label: 'Anthropic', models: ['claude-3-opus', 'claude-3-sonnet', 'claude-3-haiku'] },
    { value: 'google', label: 'Google', models: ['gemini-pro', 'gemini-ultra'] },
    { value: 'ollama', label: 'Ollama', models: ['llama2', 'mistral', 'deepseek-v2:16b'] },
    { value: 'cohere', label: 'Cohere', models: ['command', 'command-light'] },
    { value: 'mistral', label: 'Mistral AI', models: ['mistral-large', 'mistral-medium'] }
  ];

  const handleSubmit = () => {
    if (!provider || !modelName || !apiKey) {
      Alert.alert('Error', 'Please fill in all fields');
      return;
    }
    onCreateKey({ provider, modelName, apiKey });
    setProvider('openai');
    setModelName('');
    setApiKey('');
    onClose();
  };

  if (!isVisible) return null;

  return (
    <View style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <View style={[getModernCardStyle(theme, 'large'), { width: '90%', maxWidth: 500, padding: 24 }]}>
        <XStack alignItems="center" justifyContent="space-between" marginBottom={20}>
          <H2 style={{ color: getModernTextColor(theme) }}>Add AI Model API Key</H2>
          <Pressable onPress={onClose}>
            <MaterialIcons name="close" size={24} color={getModernTextColor(theme)} />
          </Pressable>
        </XStack>

        <YStack gap={16}>
          <YStack>
            <Text style={{ color: getModernTextColor(theme), marginBottom: 8, fontWeight: '600' }}>Provider</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <XStack gap={8}>
                {providers.map((p) => (
                  <Pressable
                    key={p.value}
                    onPress={() => {
                      setProvider(p.value);
                      setModelName('');
                    }}
                    style={[
                      getHeroButtonStyle(theme, provider === p.value ? 'solid' : 'outline'),
                      { paddingHorizontal: 16, paddingVertical: 10, borderRadius: 10 }
                    ]}
                  >
                    <Text style={{
                      color: provider === p.value ? HERO_TEXT_COLOR : getModernTextColor(theme),
                      fontWeight: '600'
                    }}>
                      {p.label}
                    </Text>
                  </Pressable>
                ))}
              </XStack>
            </ScrollView>
          </YStack>

          <YStack>
            <Text style={{ color: getModernTextColor(theme), marginBottom: 8, fontWeight: '600' }}>Model Name</Text>
            <Input
              value={modelName}
              onChangeText={setModelName}
              placeholder={providers.find(p => p.value === provider)?.models[0] || 'Enter model name'}
              style={{
                backgroundColor: theme ? '#f9fafb' : '#1f2937',
                borderColor: theme ? '#e5e7eb' : '#374151',
                color: getModernTextColor(theme)
              }}
            />
            {providers.find(p => p.value === provider)?.models && (
              <YStack marginTop={8} gap={4}>
                <Text style={{ color: getModernTextColor(theme, 'muted'), fontSize: 12 }}>Suggested models:</Text>
                <XStack gap={8} flexWrap="wrap">
                  {providers.find(p => p.value === provider)?.models.map((model) => (
                    <Pressable
                      key={model}
                      onPress={() => setModelName(model)}
                      style={{
                        backgroundColor: theme ? '#e5e7eb' : '#374151',
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                        borderRadius: 6
                      }}
                    >
                      <Text style={{ color: getModernTextColor(theme), fontSize: 12 }}>{model}</Text>
                    </Pressable>
                  ))}
                </XStack>
              </YStack>
            )}
          </YStack>

          <YStack>
            <Text style={{ color: getModernTextColor(theme), marginBottom: 8, fontWeight: '600' }}>API Key</Text>
            <Input
              value={apiKey}
              onChangeText={setApiKey}
              placeholder="Enter your API key"
              secureTextEntry
              style={{
                backgroundColor: theme ? '#f9fafb' : '#1f2937',
                borderColor: theme ? '#e5e7eb' : '#374151',
                color: getModernTextColor(theme)
              }}
            />
            {provider === 'ollama' && (
              <Text style={{ color: getModernTextColor(theme, 'muted'), fontSize: 12, marginTop: 4 }}>
                For Ollama, enter the base URL (e.g., http://localhost:11434)
              </Text>
            )}
          </YStack>

          <XStack gap={12} marginTop={8}>
            <Button
              onPress={onClose}
              style={[getHeroButtonStyle(theme, 'outline'), { flex: 1 }]}
            >
              <Text style={{ color: getModernTextColor(theme), fontWeight: '600' }}>Cancel</Text>
            </Button>
            <Button
              onPress={handleSubmit}
              style={[getHeroButtonStyle(theme, 'solid'), { flex: 1 }]}
            >
              <Text style={{ color: HERO_TEXT_COLOR, fontWeight: '600' }}>Add Key</Text>
            </Button>
          </XStack>
        </YStack>
      </View>
    </View>
  );
};

// Main API Screen Component
const APIScreen = () => {
  const user = selectUser();
  const theme = themeSelector();
  const { t } = useTranslation();

  // State management
  const [tokens, setTokens] = useState<TokenInfo[]>([]);
  const [loading, setLoading] = useState(true);

  // Backend API Keys State
  const [showCreateAPIKeyModal, setShowCreateAPIKeyModal] = useState(false);
  const [backendAPIKeys, setBackendAPIKeys] = useState<BackendAPIKey[]>([]);

  // Webhook State
  const [showCreateWebhookModal, setShowCreateWebhookModal] = useState(false);
  const [webhooks, setWebhooks] = useState<WebhookInfo[]>([]);
  const [availableEvents, setAvailableEvents] = useState<string[]>([]);

  // AI Model Keys State
  const [showCreateAIModelKeyModal, setShowCreateAIModelKeyModal] = useState(false);
  const [aiModelKeys, setAIModelKeys] = useState<AIModelKey[]>([]);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load OAuth tokens
      const tokensResponse = await getOAuthTokens();
      setTokens(tokensResponse.tokens || []);

      // Load API keys
      const keysResponse = await getAPIKeys();
      setBackendAPIKeys(keysResponse.apiKeys || []);

      // Load webhooks
      const webhooksResponse = await getWebhooks();
      setWebhooks(webhooksResponse.webhooks || []);

      // Load available events
      const eventsResponse = await getAvailableEvents();
      setAvailableEvents(eventsResponse.events || []);

      // Load AI model keys
      const aiModelKeysResponse = await getAIModelKeys();
      setAIModelKeys(aiModelKeysResponse.aiModelKeys || []);

    } catch (error) {
      console.error('Failed to load initial data:', error);
      Alert.alert('Error', 'Failed to load data. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };


  const handleRefreshToken = async (index: number) => {
    Alert.alert(
      'Refresh Token',
      'This will use the refresh token to get a new access token.',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Refresh', 
          onPress: async () => {
            try {
              // In a real implementation, you would call a refresh endpoint
              console.log(`Refreshing token ${index}...`);
              Alert.alert('Info', 'Token refresh functionality will be implemented soon');
            } catch (error: any) {
              Alert.alert('Error', error.message || 'Failed to refresh token');
            }
          }
        }
      ]
    );
  };

  const handleRevokeToken = async (index: number) => {
    Alert.alert(
      'Revoke Token',
      'This will permanently revoke the token. This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Revoke', 
          style: 'destructive', 
          onPress: async () => {
            try {
              await revokeOAuthToken(index);
              
              // Update local state
              setTokens(prev => prev.filter((_, i) => i !== index));
              
              Alert.alert('Success', 'Token revoked successfully');
            } catch (error: any) {
              Alert.alert('Error', error.message || 'Failed to revoke token');
            }
          }
        }
      ]
    );
  };

  // Backend API Key Handlers
  const handleCreateAPIKey = async (request: CreateAPIKeyRequest) => {
    try {
      const response = await createAPIKey(request);
      
      // Add the new key to local state
      setBackendAPIKeys(prev => [...prev, response.apiKey]);
      setShowCreateAPIKeyModal(false);
      
      Alert.alert(
        'Success', 
        'API key created successfully!\n\nIMPORTANT: This is the only time you will see the full key. Please copy it now.',
        [
          {
            text: 'Copy Key',
            onPress: () => {
              if (Platform.OS === 'web') {
                navigator.clipboard.writeText(response.apiKey.fullKey);
              } else {
                Clipboard.setString(response.apiKey.fullKey);
              }
            }
          },
          { text: 'OK' }
        ]
      );
    } catch (error: any) {
      console.error('Failed to create API key:', error);
      Alert.alert('Error', error.message || 'Failed to create API key');
    }
  };

  const handleDeleteAPIKey = async (keyId: string) => {
    try {
      await deleteAPIKey(keyId);
      
      // Remove from local state
      setBackendAPIKeys(prev => prev.filter(key => key.id !== keyId));
      
      Alert.alert('Success', 'API key deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete API key:', error);
      Alert.alert('Error', error.message || 'Failed to delete API key');
    }
  };

  const handleToggleAPIKey = async (keyId: string) => {
    try {
      const response = await toggleAPIKey(keyId);
      
      // Update local state
      setBackendAPIKeys(prev => prev.map(key => 
        key.id === keyId ? { ...key, isActive: response.isActive } : key
      ));
      
      Alert.alert('Success', response.message);
    } catch (error: any) {
      console.error('Failed to toggle API key:', error);
      Alert.alert('Error', error.message || 'Failed to toggle API key');
    }
  };

  const handleRegenerateAPIKey = async (keyId: string) => {
    Alert.alert(
      'Regenerate API Key',
      'This will create a new API key and invalidate the current one. Continue?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Regenerate',
          onPress: async () => {
            try {
              const response = await regenerateAPIKey(keyId);
              
              // Update local state
              setBackendAPIKeys(prev => prev.map(key => 
                key.id === keyId ? response.apiKey : key
              ));
              
              Alert.alert(
                'Success', 
                'API key regenerated successfully!\n\nIMPORTANT: Please copy the new key now.',
                [
                  {
                    text: 'Copy Key',
                    onPress: () => {
                      if (Platform.OS === 'web') {
                        navigator.clipboard.writeText(response.apiKey.fullKey);
                      } else {
                        Clipboard.setString(response.apiKey.fullKey);
                      }
                    }
                  },
                  { text: 'OK' }
                ]
              );
            } catch (error: any) {
              Alert.alert('Error', error.message || 'Failed to regenerate API key');
            }
          }
        }
      ]
    );
  };

  // Webhook Management Functions
  const handleCreateWebhook = async (request: CreateWebhookRequest) => {
    try {
      const response = await createWebhook(request);

      // Add the new webhook to local state
      setWebhooks(prev => [...prev, response.webhook]);

      setShowCreateWebhookModal(false);
      Alert.alert('Success', 'Webhook created successfully!');
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to create webhook');
    }
  };

  const handleDeleteWebhook = async (webhookId: string) => {
    Alert.alert(
      'Delete Webhook',
      'Are you sure you want to delete this webhook? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteWebhook(webhookId);

              // Remove from local state
              setWebhooks(prev => prev.filter(webhook => webhook.id !== webhookId));

              Alert.alert('Success', 'Webhook deleted successfully');
            } catch (error: any) {
              Alert.alert('Error', error.message || 'Failed to delete webhook');
            }
          }
        }
      ]
    );
  };

  const handleToggleWebhook = async (webhookId: string) => {
    try {
      const response = await toggleWebhook(webhookId);

      // Update local state
      setWebhooks(prev => prev.map(webhook =>
        webhook.id === webhookId ? response.webhook : webhook
      ));

      Alert.alert(
        'Success',
        `Webhook ${response.webhook.isActive ? 'enabled' : 'disabled'} successfully`
      );
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to toggle webhook');
    }
  };

  const handleTestWebhook = async (webhookId: string) => {
    try {
      const response = await testWebhook(webhookId);

      if (response.success) {
        Alert.alert(
          'Test Successful',
          `Webhook test completed successfully!\nStatus: ${response.response.status}\nResponse: ${response.response.statusText}`
        );
        
        // Refresh webhooks to get updated stats
        const webhooksResponse = await getWebhooks();
        setWebhooks(webhooksResponse.webhooks || []);
      } else {
        Alert.alert('Test Failed', response.message || 'Webhook test failed');
      }
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to test webhook');
    }
  };

  const handleCreateAIModelKey = async (request: CreateAIModelKeyRequest) => {
    try {
      const response = await createAIModelKey(request);

      // Reload AI model keys
      const aiModelKeysResponse = await getAIModelKeys();
      setAIModelKeys(aiModelKeysResponse.aiModelKeys || []);
      setShowCreateAIModelKeyModal(false);

      Alert.alert('Success', 'AI model API key created successfully!');
    } catch (error: any) {
      console.error('Failed to create AI model API key:', error);
      Alert.alert('Error', error.response?.data?.error || error.message || 'Failed to create AI model API key');
    }
  };

  const handleDeleteAIModelKey = async (keyId: string) => {
    try {
      await deleteAIModelKey(keyId);

      // Remove from local state
      setAIModelKeys(prev => prev.filter(key => key.id !== keyId));

      Alert.alert('Success', 'AI model API key deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete AI model API key:', error);
      Alert.alert('Error', error.response?.data?.error || error.message || 'Failed to delete AI model API key');
    }
  };

  const handleToggleAIModelKey = async (keyId: string) => {
    try {
      const response = await toggleAIModelKey(keyId);

      // Update local state
      setAIModelKeys(prev => prev.map(key =>
        key.id === keyId ? { ...key, isActive: response.isActive } : key
      ));

      Alert.alert('Success', response.message);
    } catch (error: any) {
      console.error('Failed to toggle AI model API key:', error);
      Alert.alert('Error', error.response?.data?.error || error.message || 'Failed to toggle AI model API key');
    }
  };

  const handleUpdateAIModelKey = async (keyId: string, updates: { isDefault?: boolean; isActive?: boolean; modelName?: string }) => {
    try {
      const response = await updateAIModelKey(keyId, updates);

      // Reload AI model keys to get updated state
      const aiModelKeysResponse = await getAIModelKeys();
      setAIModelKeys(aiModelKeysResponse.aiModelKeys || []);

      Alert.alert('Success', response.message || 'AI model API key updated successfully');
    } catch (error: any) {
      console.error('Failed to update AI model API key:', error);
      Alert.alert('Error', error.response?.data?.error || error.message || 'Failed to update AI model API key');
    }
  };

  const handleRegenerateWebhookSecret = async (webhookId: string) => {
    Alert.alert(
      'Regenerate Webhook Secret',
      'This will create a new secret and invalidate the current one. Continue?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Regenerate',
          onPress: async () => {
            try {
              const response = await regenerateWebhookSecret(webhookId);

              Alert.alert(
                'Success',
                'Webhook secret regenerated successfully!\n\nIMPORTANT: Please update your webhook endpoint with the new secret.',
                [
                  {
                    text: 'Copy Secret',
                    onPress: () => {
                      Clipboard.setString(response.webhook.secret);
                      Alert.alert('Copied', 'Secret copied to clipboard');
                    }
                  },
                  { text: 'OK' }
                ]
              );
            } catch (error: any) {
              Alert.alert('Error', error.message || 'Failed to regenerate webhook secret');
            }
          }
        }
      ]
    );
  };


  return (
    <DefaultScreen scrollDisabled style={{ flex: 1 }} containerStyle={{ paddingBottom: 50 }}>
      {loading ? (
        <View style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingVertical: 100
        }}>
          <MaterialIcons
            name="api"
            size={48}
            color={getModernTextColor(theme, 'muted')}
            style={{ marginBottom: 16 }}
          />
          <Text style={{
            color: getModernTextColor(theme),
            fontSize: 18,
            fontWeight: '600',
            marginBottom: 8,
            userSelect: 'none',
            pointerEvents: 'none'
          }}>
            Loading API Settings
          </Text>
          <Text style={{
            color: getModernTextColor(theme, 'muted'),
            fontSize: 14,
            textAlign: 'center',
            userSelect: 'none',
            pointerEvents: 'none'
          }}>
            Please wait while we fetch your configurations...
          </Text>
        </View>
      ) : (
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ paddingBottom: 100 }}
          showsVerticalScrollIndicator={false}
        >
        {/* Header */}
        <HeaderComponent
          theme={theme}
              oauthStats={{
                connectedProviders: 0,
                totalTokens: tokens.length,
                activeEndpoints: 5,
                lastSync: new Date().toLocaleTimeString()
              }}
              backendAPIKeys={backendAPIKeys}
        />

        <View style={{ paddingHorizontal: 20 }}>
              {/* Token Management Section */}
              <View style={{
                marginBottom: 32,
                userSelect: 'none'
              }}>
                <YStack>
              <TokenManager
                tokens={tokens}
                theme={theme}
                onRefreshToken={handleRefreshToken}
                onRevokeToken={handleRevokeToken}
                  />

                  {/* Quick Actions */}
                  <View style={[
                    getModernCardStyle(theme, 'medium'),
                    { marginTop: 16 }
                  ]}>
                    <Text style={{
                      color: getModernTextColor(theme),
                      fontSize: 16,
                      fontWeight: '600',
                      marginBottom: 16,
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}>
                      Quick Actions
                    </Text>

                    <YStack gap={12}>
                      <Pressable
                        style={[
                          getHeroButtonStyle(theme, 'solid'),
                          {
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingVertical: 16,
                            paddingHorizontal: 24,
                            borderRadius: 16,
                            minWidth: 200,
                            alignSelf: 'center'
                          }
                        ]}
                      >
                        <MaterialIcons name="sync" size={20} color={HERO_TEXT_COLOR} style={{ marginRight: 8 }} />
                        <Text style={{
                          color: HERO_TEXT_COLOR,
                          fontSize: 16,
                          fontWeight: '600',
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }}>
                          Sync All Tokens
                        </Text>
                      </Pressable>

                      <XStack gap={12}>
                        <Pressable
                          style={[
                            getHeroButtonStyle(theme, 'outline'),
                            {
                              flex: 1,
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'center',
                              paddingVertical: 14,
                              paddingHorizontal: 16,
                              borderRadius: 12,
                              minWidth: 140
                            }
                          ]}
                        >
                          <MaterialIcons name="download" size={18} color={getModernTextColor(theme)} style={{ marginRight: 6 }} />
                          <Text style={{
                            color: getModernTextColor(theme),
                            fontSize: 14,
                            fontWeight: '600',
                            userSelect: 'none',
                            pointerEvents: 'none'
                          }}>
                            Export Config
                          </Text>
                        </Pressable>

                        <Pressable
                          style={[
                            getHeroButtonStyle(theme, 'outline'),
                            {
                              flex: 1,
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'center',
                              paddingVertical: 14,
                              paddingHorizontal: 16,
                              borderRadius: 12,
                              minWidth: 140
                            }
                          ]}
                        >
                          <MaterialIcons name="upload" size={18} color={getModernTextColor(theme)} style={{ marginRight: 6 }} />
                          <Text style={{
                            color: getModernTextColor(theme),
                            fontSize: 14,
                            fontWeight: '600',
                            userSelect: 'none',
                            pointerEvents: 'none'
                          }}>
                            Import Config
                          </Text>
                        </Pressable>
                      </XStack>
                    </YStack>
                  </View>
                </YStack>
              </View>

              {/* Second Row: Backend API Keys Section - Full Width */}
              <View style={{
                marginBottom: 32,
                userSelect: 'none'
              }}>
                <BackendAPIKeyManager
                  apiKeys={backendAPIKeys}
                  theme={theme}
                  onDeleteKey={handleDeleteAPIKey}
                  onToggleKey={handleToggleAPIKey}
                  onRegenerateKey={handleRegenerateAPIKey}
                  onCreateNew={() => setShowCreateAPIKeyModal(true)}
                />
              </View>

              {/* Third Row: AI Model API Keys Section - Full Width */}
              <View style={{
                marginBottom: 32,
                userSelect: 'none'
              }}>
                <AIModelKeyManager
                  aiModelKeys={aiModelKeys}
                  theme={theme}
                  onDeleteKey={handleDeleteAIModelKey}
                  onToggleKey={handleToggleAIModelKey}
                  onUpdateKey={handleUpdateAIModelKey}
                  onCreateNew={() => setShowCreateAIModelKeyModal(true)}
                />
              </View>

              {/* Fourth Row: Webhook Management Section - Full Width */}
              <View style={{
                marginBottom: 32,
                userSelect: 'none'
              }}>
                <WebhookManager
                  webhooks={webhooks}
                  theme={theme}
                  onDeleteWebhook={handleDeleteWebhook}
                  onToggleWebhook={handleToggleWebhook}
                  onTestWebhook={handleTestWebhook}
                  onRegenerateSecret={handleRegenerateWebhookSecret}
                  onCreateNew={() => setShowCreateWebhookModal(true)}
                />
              </View>

        </View>
      </ScrollView>
      )}

      {/* Create API Key Modal */}
      <APIKeyCreator
        isVisible={showCreateAPIKeyModal}
        onClose={() => setShowCreateAPIKeyModal(false)}
        onCreateKey={handleCreateAPIKey}
        theme={theme}
      />

      {/* Create Webhook Modal */}
      <WebhookCreator
        isVisible={showCreateWebhookModal}
        onClose={() => setShowCreateWebhookModal(false)}
        onCreateWebhook={handleCreateWebhook}
        availableEvents={availableEvents}
        theme={theme}
      />

      {/* Create AI Model Key Modal */}
      <AIModelKeyCreator
        isVisible={showCreateAIModelKeyModal}
        onClose={() => setShowCreateAIModelKeyModal(false)}
        onCreateKey={handleCreateAIModelKey}
        theme={theme}
      />
    </DefaultScreen>
  );
};

export default APIScreen;
